import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  CloudArrowUp, MagnifyingGlass, CheckCircle, XCircle,
  LinkSimple, Info, Package, Buildings, CalendarBlank, IdentificationCard, MapPin, FileText, X, Warning,
  CircleNotch, Circle, PushPin, ClockCounterClockwise, CaretDown, CaretRight,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api, { API_BASE } from "../lib/api";

// Ordered milestones shown in the live "Push to SAP" progress panel.
const PUSH_STEPS = [
  { key: "validate", label: "Validate order" },
  { key: "create", label: "Create Sales Order in SAP" },
  { key: "stamp", label: "Stamp header + End Customer fields" },
  { key: "verify", label: "Read back & verify in SAP" },
  { key: "done", label: "Order created & verified" },
];

// SAP Push list filters (customer dropdown, PO date range, "open only" toggle). Enabled.
const SAP_PUSH_FILTERS_DRAFT = true;

// Transfer-pricing variance: same rule as the Transfer Pricing page — |Δ| > 15% vs the PO price
// is flagged for review. variancePct = (transfer_rate - po_price) / po_price * 100.
const TP_VAR_THRESHOLD = 15;
const _n = (v) => (v === "" || v == null ? NaN : Number(v));
const tpVariancePct = (rate, po) => {
  const r = _n(rate), p = _n(po);
  return p && !Number.isNaN(r) && !Number.isNaN(p) ? ((r - p) / p) * 100 : null;
};

const _MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// App-standard display format: DD-MMM-YY (e.g. 07-Jul-26). Input/stored value stays ISO yyyy-mm-dd.
const fmtDMY = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}-${_MONTHS[Number(m) - 1] || m}-${y.slice(2)}`;
};

// Progress History — a collapsible audit table of every SAP push and its milestone timeline.
// Rendered both on the empty SAP Push screen (global audit) and inside a selected PO's detail view.
const PushHistoryPanel = ({ history, historyOpen, setHistoryOpen, historyLoading, loadHistory, historyExpanded, setHistoryExpanded }) => (
  <div data-testid="sap-push-history" style={{ marginBottom: 12, borderRadius: 10, border: "1px solid var(--omh-border,#e2e8f0)", overflow: "hidden" }}>
    <button data-testid="sap-push-history-toggle" onClick={() => { setHistoryOpen((o) => !o); if (!historyOpen) loadHistory(); }}
      style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", background: "#F8FAFC", border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 700, color: "#334155" }}>
      {historyOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
      <ClockCounterClockwise size={15} weight="bold" color="#0EA5E9" />
      Progress History
      <span style={{ fontWeight: 600, color: "#94A3B8" }}>· last {history.length} push{history.length === 1 ? "" : "es"}</span>
      <span style={{ marginLeft: "auto", fontSize: 11, color: "#94A3B8" }}>{historyLoading ? "loading…" : "click a row to see how it was done"}</span>
    </button>
    {historyOpen && (
      <div data-testid="sap-push-history-body" style={{ maxHeight: 340, overflowY: "auto", borderTop: "1px solid var(--omh-border,#e2e8f0)" }}>
        {history.length === 0 && !historyLoading && (
          <div style={{ padding: 16, fontSize: 12, color: "#94A3B8" }}>No pushes recorded yet.</div>
        )}
        {history.map((h) => {
          const open = historyExpanded === h.id;
          const verifyOk = h.header_verify?.ok;
          const statusColor = h.ok ? (verifyOk === false ? "#B45309" : "#15803d") : "#B91C1C";
          const statusIcon = h.ok ? (verifyOk === false ? <Warning size={14} weight="fill" color="#B45309" /> : <CheckCircle size={14} weight="fill" color="#15803d" />) : <XCircle size={14} weight="fill" color="#B91C1C" />;
          return (
            <div key={h.id} data-testid={`sap-history-row-${h.id}`} style={{ borderBottom: "1px solid #F1F5F9" }}>
              <button data-testid={`sap-history-toggle-${h.id}`} onClick={() => setHistoryExpanded(open ? null : h.id)}
                style={{ width: "100%", display: "grid", gridTemplateColumns: "18px 100px 1fr auto", alignItems: "center", gap: 10, padding: "8px 12px", background: open ? "#F8FAFC" : "white", border: "none", cursor: "pointer", textAlign: "left", fontSize: 12 }}>
                {open ? <CaretDown size={12} weight="bold" color="#94A3B8" /> : <CaretRight size={12} weight="bold" color="#94A3B8" />}
                <span style={{ fontFamily: "monospace", fontWeight: 700, color: h.order_id ? "#0369A1" : "#94A3B8" }}>{h.order_id ? `#${h.order_id}` : "—"}</span>
                <span style={{ color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {h.end_customer?.name || h.account_id || "—"}
                  {h.transfer_party && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#0F766E", background: "#CCFBF1", borderRadius: 999, padding: "1px 7px" }}>TP {h.tp_code || ""}</span>}
                  <span style={{ marginLeft: 8, color: "#94A3B8" }}>{h.at ? fmtDMY(h.at) : ""}</span>
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: statusColor, fontWeight: 700 }}>
                  {statusIcon}
                  {h.ok ? (verifyOk === false ? "unverified" : "verified") : "rejected"}
                </span>
              </button>
              {open && (
                <div data-testid={`sap-history-timeline-${h.id}`} style={{ padding: "4px 14px 12px 40px", background: "#F8FAFC" }}>
                  {h.by && <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 6 }}>by {h.by}{h.external_id ? ` · PO ${h.external_id}` : ""}{h.reason ? ` · ${h.reason}` : ""}</div>}
                  {(h.milestones || []).length === 0 ? (
                    <div style={{ fontSize: 11.5, color: "#94A3B8" }}>No timeline captured for this push.</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {h.milestones.map((m, i) => {
                        const mic = m.status === "done" ? <CheckCircle size={14} weight="fill" color="#22C55E" />
                          : m.status === "error" ? <XCircle size={14} weight="fill" color="#EF4444" />
                          : m.status === "warn" ? <Warning size={14} weight="fill" color="#F59E0B" />
                          : <Circle size={14} weight="bold" color="#CBD5E1" />;
                        const blanks = m.still_blank || [];
                        return (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: "#475569" }}>
                            {mic}
                            <span style={{ fontWeight: 600 }}>{m.label}</span>
                            {m.order_id && <span style={{ fontFamily: "monospace", color: "#0369A1" }}>#{m.order_id}</span>}
                            {m.attempts > 1 && <span style={{ color: "#CA8A04" }}>re-stamped x{m.attempts}</span>}
                            {blanks.length > 0 && <span style={{ color: "#DC2626" }}>still blank: {blanks.join(", ")}</span>}
                            {m.message && <span style={{ color: "#DC2626" }}>{m.message}</span>}
                            {m.at && <span style={{ marginLeft: "auto", color: "#CBD5E1", fontSize: 10.5 }}>{String(m.at).slice(11, 19)}</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {h.end_customer && (h.end_customer.code || h.end_customer.name) && (
                    <div style={{ marginTop: 8, fontSize: 11, color: "#64748B" }}>
                      End Customer: <b>{h.end_customer.name || h.end_customer.code}</b>
                      {h.end_customer.code && h.end_customer.name ? ` (${h.end_customer.code})` : ""}
                      {h.end_customer.po_ref ? ` · PO ${h.end_customer.po_ref}` : ""}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </div>
);

// Sales orgs valid for EVERY line that declares a set (intersection). Empty => no constraint / no single common org.
const lineCommonSalesOrgs = (lines) => {
  const sets = (lines || []).map((l) => l.sales_orgs).filter((a) => Array.isArray(a) && a.length);
  if (!sets.length) return [];
  return sets.reduce((acc, s) => acc.filter((x) => s.includes(x)), sets[0].slice());
};
// Keep `preferred` sales org if it's valid for all lines, else force to the first org valid for every line.
const resolveSellerForLines = (preferred, lines) => {
  const common = lineCommonSalesOrgs(lines);
  if (!common.length) return preferred;
  if (preferred && common.includes(preferred)) return preferred;
  return common[0];
};

// Editable date field that shows DD-MMM-YY but keeps the native calendar picker.
function DateField({ value, onChange, testid, placeholder = "— select —" }) {
  return (
    <div className="jde-input" style={{ position: "relative", display: "flex", alignItems: "center", padding: "0 10px", cursor: "pointer" }}>
      <span data-testid={testid ? `${testid}-display` : undefined} style={{ color: value ? "var(--omh-text)" : "var(--omh-text-soft)", pointerEvents: "none" }}>
        {value ? fmtDMY(value) : placeholder}
      </span>
      <input
        type="date"
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch { /* ignore */ } }}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer", border: "none", background: "transparent" }}
      />
    </div>
  );
}

// One Ship-To / Bill-To resolution row. When the PO carries address text we show the scored
// SAP candidates (best first). When it doesn't, we fall back to a COLLAPSED manual picker of the
// account's full SAP address book so the user can still stamp an address. "" = don't send.
const _confTone = (c) => (c === "High" ? "#059669" : c === "Medium" ? "#B45309" : "#DC2626");
const _addrLine = (a) => [a?.care_of, a?.street, a?.street2, a?.city, a?.region, a?.postal, a?.country].filter(Boolean).join(", ") || a?.formatted || "(unnamed address)";

// Custom address dropdown — a native <select> truncates long addresses and can't wrap, so we render
// our own: a trigger button + a wide, scrollable panel where each option shows the FULL address on
// multiple lines. Keeps the same {key,label,address} option shape the callers already build.
function AddressPicker({ kind, opts, selKey, onSel }) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [query, setQuery] = useState("");
  // Position the menu with FIXED coords from the trigger's rect so it renders in a portal on
  // document.body — escaping the `.sp-section { overflow:hidden }` clip (and any scroll ancestor).
  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const desired = 320;
    const openUp = spaceBelow < Math.min(desired, 220) && spaceAbove > spaceBelow;
    setMenuStyle({
      position: "fixed",
      left: Math.round(r.left),
      width: Math.round(Math.max(r.width, 340)),
      maxHeight: Math.round(Math.max(160, Math.min(desired, (openUp ? spaceAbove : spaceBelow) - 12))),
      ...(openUp ? { bottom: Math.round(window.innerHeight - r.top + 4) } : { top: Math.round(r.bottom + 4) }),
    });
  }, []);
  useEffect(() => {
    if (!open) return;
    place();
    const onDoc = (e) => {
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onReflow = () => place();
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [open, place]);
  const current = opts.find((o) => o.key === selKey);
  const currentLabel = current ? current.label : "— Don't send (let SAP use its default) —";
  const pick = (k) => { onSel(k); setOpen(false); };
  const needle = query.trim().toLowerCase();
  const shown = needle ? opts.filter((o) => (o.label || "").toLowerCase().includes(needle)) : opts;
  const menu = (open && menuStyle) ? createPortal(
    <div ref={menuRef} data-testid={`sap-resolve-${kind}-menu`} role="listbox"
      style={{ ...menuStyle, zIndex: 3000, display: "flex", flexDirection: "column", background: "#fff",
        border: "1px solid var(--omh-border,#CBD5E1)", borderRadius: 10,
        boxShadow: "0 12px 28px rgba(15,23,42,0.18)" }}>
      <div style={{ padding: 8, borderBottom: "1px solid #E2E8F0", background: "#F8FAFC", flexShrink: 0 }}>
        <input autoFocus data-testid={`sap-resolve-${kind}-search`} value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Type to filter ${opts.length} addresses…`}
          style={{ width: "100%", padding: "7px 10px", border: "1px solid var(--omh-border,#CBD5E1)",
            borderRadius: 6, fontSize: 12.5, outline: "none", boxSizing: "border-box" }} />
      </div>
      <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
        <button type="button" data-testid={`sap-resolve-${kind}-opt-none`} onClick={() => pick("")}
          style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", border: "none",
            borderBottom: "1px solid #F1F5F9", background: selKey === "" ? "#F0F9FF" : "#fff", cursor: "pointer",
            fontSize: 12, color: "var(--omh-text-muted,#64748B)" }}>
          — Don't send (let SAP use its default) —
        </button>
        {shown.map((o) => (
          <button key={o.key} type="button" data-testid={`sap-resolve-${kind}-opt-${o.key}`} onClick={() => pick(o.key)}
            style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", border: "none",
              borderBottom: "1px solid #F1F5F9", background: o.key === selKey ? "#F0F9FF" : "#fff", cursor: "pointer",
              fontSize: 12.5, lineHeight: 1.45, whiteSpace: "normal", wordBreak: "break-word",
              color: "var(--omh-text,#0F172A)" }}>
            {o.label}
          </button>
        ))}
        {!shown.length && (
          <div data-testid={`sap-resolve-${kind}-noresults`}
            style={{ padding: "10px 12px", fontSize: 12, color: "var(--omh-text-muted,#64748B)" }}>
            No addresses match &ldquo;{query}&rdquo;
          </div>
        )}
      </div>
    </div>, document.body) : null;
  return (
    <div style={{ position: "relative" }} data-testid={`sap-resolve-${kind}-picker`}>
      <button ref={triggerRef} type="button" data-testid={`sap-resolve-${kind}-trigger`}
        onClick={() => setOpen((v) => { if (!v) setQuery(""); return !v; })}
        style={{ width: "100%", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8,
          padding: "8px 10px", border: "1px solid var(--omh-border,#CBD5E1)", borderRadius: 8, background: "#fff",
          cursor: "pointer", fontSize: 12.5, textAlign: "left", color: "var(--omh-text,#0F172A)", lineHeight: 1.4 }}>
        <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{currentLabel}</span>
        <CaretDown size={14} weight="bold" style={{ flexShrink: 0, opacity: 0.6, marginTop: 2 }} />
      </button>
      {menu}
    </div>
  );
}
function ResolveRow({ kind, label, block, sapAddresses, value, onPick, twinSource }) {
  const cands = block?.candidates || [];
  const best = block?.best;
  const manual = cands.length === 0;                       // no PO text → manual pick mode
  const opts = cands.length
    ? cands.map((c, i) => ({ key: c.address?.uuid || `c${i}`, address: c.address,
        label: `${c.confidence} · ${c.score}/100 · ${c.usage} · ${_addrLine(c.address)}` }))
    : (sapAddresses || []).map((a, i) => ({ key: a.uuid || `m${i}`, address: a,
        label: `${a.usage || "Default"} · ${_addrLine(a)}` }));
  const selKey = value ? (opts.find((o) => o.address === value)?.key || value.uuid || "") : "";
  const onSel = (k) => onPick(k === "" ? null : (opts.find((o) => o.key === k)?.address || null));
  const picker = <AddressPicker kind={kind} opts={opts} selKey={selKey} onSel={onSel} />;
  return (
    <div className="sp-fld" style={{ gridColumn: "1 / -1" }} data-testid={`sap-resolve-${kind}`}>
      <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <MapPin size={13} weight="bold" /> {label}
      </label>
      {/* The address that WILL be sent — shown plainly. Dropdown below only to change it. */}
      <div data-testid={`sap-resolve-${kind}-chosen`} style={{ fontSize: 13, color: "var(--omh-text)", padding: "2px 0 2px" }}>
        {value
          ? <><span style={{ color: "var(--omh-text-muted)" }}>PO default: </span><b>{_addrLine(value)}</b></>
          : <><span style={{ color: "var(--omh-text-muted)" }}>Default: </span><b>Don't send</b> <span className="sp-hint" style={{ display: "inline" }}>(SAP uses its own default)</span></>}
      </div>
      {twinSource && block?.po_text && (
        <div className="sp-hint" data-testid={`sap-resolve-${kind}-twin`} style={{ color: "var(--omh-primary,#2A5C9B)", marginBottom: 2 }}>
          <LinkSimple size={12} weight="bold" style={{ verticalAlign: -2 }} /> Borrowed from linked record <b>{twinSource.po_number}</b>
        </div>
      )}
      {opts.length > 0 ? (
        <details data-testid={`sap-resolve-${kind}-change`} style={{ marginTop: 2 }}>
          <summary style={{ cursor: "pointer", fontSize: 11.5, color: "var(--omh-primary,#2A5C9B)", fontWeight: 600 }}>
            To change, select an address
          </summary>
          <div style={{ marginTop: 6 }}>{picker}</div>
        </details>
      ) : (
        <div className="sp-hint" data-testid={`sap-resolve-${kind}-none`}>No SAP addresses on file for this account.</div>
      )}
    </div>
  );
}

export default function SapPush() {
  const [conn, setConn] = useState(null);
  const [q, setQ] = useState("");
  const [custFilter, setCustFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [unpushed, setUnpushed] = useState(false);
  const [poCustomers, setPoCustomers] = useState([]);
  const [pos, setPos] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState("general");   // segmented detail tabs (no-scroll layout)
  const tabShow = (t) => (tab === t ? undefined : "none");
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [pushSteps, setPushSteps] = useState(null);   // live "Push to SAP" milestones {key:{status,label,detail}}
  const [pushElapsed, setPushElapsed] = useState(0);  // seconds elapsed for the in-flight push (SAP commits vary ~20–90s)
  const pushTimerRef = useRef(null);
  const [defaults, setDefaults] = useState({ sales_unit: "", distribution_channel: "01", employee_id: "", site_id: "" });
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  // Sales-area (Sales Unit / Distribution Channel / Ship-From) read from the customer's
  // recent SAP Sales Orders (+ part match) — drives auto-fill, candidate dropdowns and a "why" hint.
  const [salesArea, setSalesArea] = useState(null);
  const [salesAreaLoading, setSalesAreaLoading] = useState(false);
  // Note shown when the Sales Org was auto-switched to satisfy the PO's line items ({from, to}).
  const [soAutoFix, setSoAutoFix] = useState(null);
  // Snapshot of this PO's SAVED customer-map sales area, for the "Mapping Auto-Correct" banner
  // (compares the stored map against what live SAP history actually uses).
  const [mapInfo, setMapInfo] = useState(null);
  const [mapSaving, setMapSaving] = useState(false);
  // Ship-To / Bill-To resolution against live SAP (from the PO Address Resolver), inline here.
  const [addrRes, setAddrRes] = useState(null);
  const [addrLoading, setAddrLoading] = useState(false);
  const [shipSel, setShipSel] = useState(null);   // null = don't send; else the chosen SAP address object
  const [billSel, setBillSel] = useState(null);
  // When routed via a transfer party, the Bill-To switches to the transfer party's (AV MATICS)
  // own SAP address book — the order is both sold-to and billed-to AV MATICS. null = direct.
  const [tpBillAddrs, setTpBillAddrs] = useState(null);
  const [tpBillLoading, setTpBillLoading] = useState(false);
  const [tpBillPinned, setTpBillPinned] = useState(null);   // pinned default Bill-To address for the active TP party
  const [pinning, setPinning] = useState(false);
  // Progress History — a table of past SAP pushes with their full milestone timeline (auditable).
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(null);  // log id of the expanded timeline row
  const [tpSource, setTpSource] = useState(null);   // how the current routing was chosen: memory|dispatch|master|manual
  // Stored original PO document viewer (reuses the /po/{id}/file + /file-text endpoints).
  const [fileOpen, setFileOpen] = useState(false);
  const [fileState, setFileState] = useState("idle");  // idle | loading | ok | none | error
  const [fileUrl, setFileUrl] = useState("");
  const [fileMime, setFileMime] = useState("");
  const [fileText, setFileText] = useState("");
  const [fileName, setFileName] = useState("");
  // Step 2 of the robust push: SAP read-back of the created order (what's actually written) + release.
  const [verify, setVerify] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [releasing, setReleasing] = useState(false);
  // True once the user manually edits the Ship-From site — stops SAP auto-defaulting from overriding it.
  const [siteTouched, setSiteTouched] = useState(false);
  // --- Transfer-party routing (sold-to AV MATICS at transfer rates; end customer stamped;
  // goods drop-shipped). Mirrors Dispatch Planning's "Route via transfer party?" control. ---
  const [tpOptions, setTpOptions] = useState([]);   // available transfer parties (e.g. AVM)
  const [custTp, setCustTp] = useState(null);       // {transfer_party, tp_code, tp_name} for the PO's customer
  const [tpRouting, setTpRouting] = useState(null); // applied reroute result from /so/transfer-routing
  const [tpLoading, setTpLoading] = useState(false);
  const directSnapshot = useRef(null);              // {account_id, prices[]} to restore when reverting to direct

  useEffect(() => { api.get("/sap/status").then((r) => { setConn(r.data); if (r.data?.defaults) setDefaults(r.data.defaults); }).catch(() => setConn((prev) => (prev?.ok ? prev : { ok: false, reason: "unreachable" }))); }, []);
  useEffect(() => { api.get("/sap/suggest/employee?limit=500").then((r) => setEmployees(r.data || [])).catch(() => setEmployees([])); }, []);
  useEffect(() => { api.get("/sap/po-customers").then((r) => setPoCustomers(r.data || [])).catch(() => setPoCustomers([])); }, []);
  useEffect(() => { api.get("/dispatch/transfer-party-options").then((r) => setTpOptions(r.data || [])).catch(() => setTpOptions([])); }, []);

  // Apply a transfer-party reroute: fetch the resolution (AV MATICS SAP account, end customer,
  // approved transfer rates) then rewrite the form (AccountParty → AV MATICS, End Customer stamp,
  // line prices → transfer rates, Ship-To PartyID → end customer for drop-ship). Blocked lines
  // (no valid transfer price) are flagged and the push is disabled until they're fixed.
  const applyRouting = async (poId, tpc) => {
    if (!poId || !tpc) return;
    setTpLoading(true);
    try {
      const { data } = await api.get(`/sap/so/transfer-routing/${poId}?tp_code=${encodeURIComponent(tpc)}`);
      setTpRouting(data);
      if (!data.ok) { toast.error(data.reason || "Transfer-party routing unavailable"); return; }
      setForm((f) => {
        if (!f || f.app_po_id !== poId) return f;
        const newLines = f.lines.map((l, i) => {
          const rl = (data.lines || [])[i];
          return rl && rl.transfer_rate != null ? { ...l, price: rl.transfer_rate } : { ...l, price: "" };
        });
        return { ...f, account_id: data.account_id, ship_to_party_id: data.end_customer_account || "",
          end_customer_code: data.end_customer?.code || f.end_customer_code || "",
          end_customer_name: data.end_customer?.name || f.end_customer_name || "",
          end_customer_po_ref: data.end_customer?.po_ref || f.external_id || f.end_customer_po_ref || "",
          lines: newLines };
      });
      if (data.lines_blocked > 0) {
        toast.error(`Routed via ${data.tp_name}, but ${data.lines_blocked} line(s) have no approved transfer price — fix them before pushing.`);
      } else {
        toast.success(`Routing via ${data.tp_name} · end customer ${data.end_customer?.name || data.end_customer?.code}`);
      }
      // Bill-To follows the sold-to party: load AV MATICS's own SAP address book and default the
      // Bill-To to its billing address (Ship-To stays on the end customer for drop-ship). A pinned
      // default Bill-To for the party wins, so it stops drifting to a branch address.
      setTpBillAddrs([]);
      setTpBillPinned(null);
      setTpBillLoading(true);
      const partyKey = data.account_id || tpc;
      Promise.all([
        api.get(`/sap/customers/${encodeURIComponent(partyKey)}/addresses`).then((r) => r.data || {}).catch(() => ({})),
        api.get(`/sap/so/bill-to-default/${encodeURIComponent(data.tp_code || tpc)}`).then((r) => r.data || {}).catch(() => ({})),
      ])
        .then(([d, pin]) => {
          const addrs = d.addresses || [];
          setTpBillAddrs(addrs);
          if (pin && pin.address) {
            setTpBillPinned(pin.address);
            setBillSel(pin.address);
          } else {
            // Default Bill-To: a dedicated BILL_TO, else the Main/Account (registered) address,
            // else any — avoids defaulting to a drop-ship/Ship-To address for the billing party.
            setBillSel((d.bill_to && d.bill_to[0]) || (d.other && d.other[0]) || addrs[0] || null);
          }
        })
        .catch(() => { setTpBillAddrs([]); setBillSel(null); })
        .finally(() => setTpBillLoading(false));
    } catch { toast.error("Transfer-party routing failed"); setTpRouting(null); }
    finally { setTpLoading(false); }
  };

  // Revert to a direct order: restore the PO customer account + PO line prices captured at pick time,
  // and put the Bill-To back on the end customer's resolved address book.
  const revertRouting = () => {
    const snap = directSnapshot.current;
    setTpRouting(null);
    setTpBillAddrs(null);
    setBillSel(addrRes?.bill_to?.best && addrRes.bill_to.best.score >= 40 ? addrRes.bill_to.best.address : null);
    setForm((f) => {
      if (!f) return f;
      const newLines = f.lines.map((l, i) => ({ ...l, price: snap ? (snap.prices[i] ?? "") : l.price }));
      return { ...f, account_id: snap?.account_id || f.account_id, ship_to_party_id: "",
        end_customer_code: sel?.app_customer_code || "",
        end_customer_name: sel?.customer_name || "",
        end_customer_po_ref: sel?.po_number || "",
        lines: newLines };
    });
  };

  // Routing Memory: persist the customer's transfer-party choice so repeat orders default correctly.
  const savePref = (route, tp_code, tp_name) => {
    const pc = sel?.app_customer_code;
    if (!pc) return;
    api.put(`/sap/so/routing-pref/${encodeURIComponent(pc)}`, { route, tp_code: tp_code || null, tp_name: tp_name || null }).catch(() => {});
  };
  // User-initiated routing choices (remember them). Auto-defaults call applyRouting/revertRouting directly.
  const chooseTp = (tpc, name) => { setTpSource("manual"); applyRouting(form?.app_po_id, tpc); savePref("tp", tpc, name); };
  const chooseDirect = () => { setTpSource("manual"); revertRouting(); savePref("direct"); };

  const tpActive = !!(tpRouting && tpRouting.ok);
  const tpBlocked = !!(tpRouting && (!tpRouting.ok || (tpRouting.lines_blocked || 0) > 0));
  // Lines whose transfer rate deviates > threshold from the PO price (review before pushing).
  const tpHighVar = tpActive
    ? (tpRouting.lines || []).reduce((n, ln, i) => {
        if (ln.needs_transfer_price) return n;
        const v = tpVariancePct(ln.transfer_rate, directSnapshot.current?.prices?.[i]);
        return v != null && Math.abs(v) > TP_VAR_THRESHOLD ? n + 1 : n;
      }, 0)
    : 0;
  // How the active routing was chosen — shown as a small chip so the default is explainable.
  const TP_SRC = { memory: "Remembered", dispatch: "From dispatch plan", master: "Customer default", manual: "Manual" };
  const tpSrcChip = tpActive && tpSource ? (
    <span data-testid="sap-tp-source" style={{ marginLeft: 6, fontSize: 8.5, fontWeight: 700, background: "#E0F2FE", color: "#075985", border: "1px solid #BAE6FD", borderRadius: 4, padding: "1px 5px", textTransform: "uppercase" }}>{TP_SRC[tpSource] || tpSource}</span>
  ) : null;

  // Backfill sales-area defaults (incl. permanent Employee Responsible) onto the selected PO
  // once /sap/status resolves — covers the case where a PO was picked before defaults loaded.
  useEffect(() => {
    setForm((f) => (f ? {
      ...f,
      seller_id: f.seller_id || defaults.sales_unit || "",
      distribution_channel: f.distribution_channel || defaults.distribution_channel || "01",
      employee_id: f.employee_id || defaults.employee_id || "",
      site_id: f.site_id || defaults.site_id || "",
    } : f));
  }, [defaults]);
  // Reconcile the list against SAP: for POs with no locally-logged SO, check SAP LIVE (in parallel)
  // and fill the "SAP SO #" badge if the order actually exists there (created via legacy C#,
  // read-back recovery, or directly in SAP). Non-blocking — the list renders first, badges fill in.
  const reconcileSapLinks = (list) => {
    const refs = [...new Set((list || [])
      .filter((p) => !p.sap_so_id && p.po_number)
      .map((p) => String(p.po_number)))];
    if (!refs.length) return;
    api.get(`/sap/so/exists-batch?refs=${encodeURIComponent(refs.join(","))}`)
      .then((r) => {
        const m = r.data || {};
        setPos((cur) => cur.map((p) => {
          const hit = !p.sap_so_id && p.po_number ? m[String(p.po_number)] : null;
          return hit && hit.exists && hit.order_id
            ? { ...p, sap_so_id: hit.order_id, sap_so_reconciled: true }
            : p;
        }));
      })
      .catch(() => {});
  };

  const search = () => {
    const p = new URLSearchParams({ q, limit: "500" });
    if (custFilter) p.set("customer", custFilter);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (unpushed) p.set("unpushed_only", "true");
    return api.get(`/sap/pos?${p.toString()}`)
      .then((r) => { const list = r.data || []; setPos(list); reconcileSapLinks(list); })
      .catch(() => setPos([]));
  };
  useEffect(() => { const t = setTimeout(search, 350); return () => clearTimeout(t); }, [q, custFilter, dateFrom, dateTo, unpushed]);
  const clearFilters = () => { setQ(""); setCustFilter(""); setDateFrom(""); setDateTo(""); setUnpushed(false); };
  const activeFilters = (custFilter ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0) + (unpushed ? 1 : 0);

  // Ship-From Site (one per order): ByD determines valid sending locations from the ordered
  // product. Query on the FIRST line's product; auto-pick when SAP returns exactly one site.
  const firstProduct = form?.lines?.[0]?.product_id || "";
  useEffect(() => {
    if (!firstProduct) { setSites([]); return; }
    let cancelled = false;
    setSitesLoading(true);
    api.get(`/sap/site-suggestions?product_id=${encodeURIComponent(firstProduct)}`)
      .then((r) => {
        if (cancelled) return;
        const rows = r.data?.sites || [];
        setSites(rows);
        // Default the Ship-From site from SAP master data (product's valid site / determination /
        // most-used history) — overrides the weaker Sales-Unit-prefix guess, unless the user has
        // manually changed it. Still fully editable afterwards.
        const def = r.data?.default || (rows.length === 1 ? rows[0].id : "");
        if (def) setForm((f) => (f && !siteTouched ? { ...f, site_id: def } : f));
      })
      .catch(() => { if (!cancelled) setSites([]); })
      .finally(() => { if (!cancelled) setSitesLoading(false); });
    return () => { cancelled = true; };
  }, [firstProduct]);

  // The Ship-From site mirrors the Sales Unit prefix (P4-SAL -> P4, P1-SAL -> P1, P9-SAL -> P9).
  const siteFromSalesUnit = (su) => {
    const m = /^(P\d+)\b/i.exec((su || "").trim());
    return m ? m[1].toUpperCase() : "";
  };

  // "Why" hint for a sales-area field, explaining the SAP-history basis of the auto-fill.
  const SaHint = ({ fld }) => {
    if (salesAreaLoading) return <div className="sp-hint">Reading this customer's recent SAP orders…</div>;
    const b = salesArea?.ok ? salesArea[fld] : null;
    if (!b || !b.value) return null;
    const acct = salesArea.account;
    const partBased = fld === "ship_from_site" && salesArea.ship_from_site?.basis === "parts";
    const scope = partBased ? "orders with these parts" : `recent ${acct} orders`;
    const color = b.confidence === "high" ? "var(--omh-ok,#15803d)"
      : b.confidence === "low" ? "var(--omh-warn,#b45309)" : "var(--omh-muted,#64748b)";
    return (
      <div className="sp-hint" data-testid={`sap-sa-hint-${fld}`} style={{ color }}>
        From SAP · <b>{b.count}</b> of {b.total} {scope}
        {b.candidates && b.candidates.length > 1 &&
          ` · also seen: ${b.candidates.slice(1).map((c) => `${c.value} (${c.count})`).join(", ")}`}
      </div>
    );
  };

  // "Mapping Auto-Correct": does the saved customer map disagree with live SAP history?
  const sa = salesArea?.ok ? salesArea : null;
  const sapSU = sa?.sales_unit?.value || "";
  const sapDC = sa?.distribution_channel?.value || "";
  const suConfident = sa && (sa.sales_unit?.confidence === "high" || sa.sales_unit?.confidence === "medium");
  const mappedSU = mapInfo?.mapped_su || "";
  const mappedDC = mapInfo?.mapped_dc || "";
  const suDrift = suConfident && sapSU && mappedSU && mappedSU !== sapSU;
  const dcDrift = sapDC && mappedDC && mappedDC !== sapDC;
  const noMap = suConfident && sapSU && !mappedSU;
  const showMapFix = !!(mapInfo?.app_code && (suDrift || dcDrift || noMap));

  // Context-aware Sales Org dropdown: orgs valid for these items first, then SAP-history orgs,
  // then the mapped/current value. Each option is labelled so the user understands the choice.
  const lineCommon = lineCommonSalesOrgs(sel?.lines || []);
  const lineValidSOs = [...new Set((sel?.lines || []).flatMap((l) => l.sales_orgs || []))];
  const soHistCount = {};
  (salesArea?.sales_unit?.candidates || []).forEach((c) => { soHistCount[c.value] = c.count; });
  const soOptionVals = [...new Set([
    ...lineCommon,                       // valid for ALL items (preferred)
    ...lineValidSOs,                     // valid for some items
    ...Object.keys(soHistCount),         // this customer's SAP history
    form?.expected_seller,
    form?.seller_id,
  ].filter(Boolean))];
  const soLabel = (v) => {
    const tags = [];
    if (lineCommon.includes(v)) tags.push("valid for items");
    else if (lineValidSOs.includes(v)) tags.push("valid for some items");
    if (soHistCount[v] != null) tags.push(`SAP history ${soHistCount[v]}×`);
    return tags.length ? `${v} · ${tags.join(" · ")}` : v;
  };

  const applyMapCorrection = () => {
    if (!mapInfo?.app_code || !sapSU) return;
    setMapSaving(true);
    api.post("/sap/map/customers/sales-area", {
      app_code: mapInfo.app_code, sales_unit: sapSU, distribution_channel: sapDC,
    })
      .then(() => {
        setMapInfo((m) => (m ? { ...m, mapped_su: sapSU, mapped_dc: sapDC || m.mapped_dc, saved: true } : m));
        setForm((f) => (f ? { ...f, expected_seller: sapSU } : f));
      })
      .catch(() => setMapInfo((m) => (m ? { ...m, saveError: true } : m)))
      .finally(() => setMapSaving(false));
  };

  const pick = (p) => {
    setSel(p);
    setResult(null);
    setTab("general");
    setAddrRes(null); setShipSel(null); setBillSel(null);
    setAddrLoading(true);
    // Resolve the PO's captured Ship-To / Bill-To against live SAP addresses (read-only).
    api.get(`/sap/address-resolver/${p.id}`)
      .then((r) => {
        setAddrRes(r.data);
        // Auto-select a confident match (>=40) so the common case needs no clicks.
        setShipSel(r.data?.ship_to?.best && r.data.ship_to.best.score >= 40 ? r.data.ship_to.best.address : null);
        setBillSel(r.data?.bill_to?.best && r.data.bill_to.best.score >= 40 ? r.data.bill_to.best.address : null);
      })
      .catch(() => setAddrRes(null))
      .finally(() => setAddrLoading(false));
    const su = p.sales_unit || defaults.sales_unit || "";
    // Products may only be sellable under a specific sales org. If the customer's usual org can't
    // sell every line, auto-switch to the one that's valid for all items (user confirmed behaviour).
    const seller = resolveSellerForLines(su, p.lines);
    if (seller && seller !== su) setSoAutoFix({ from: su, to: seller });
    setForm({
      app_po_id: p.id,
      external_id: p.po_number || "",
      name: "",  // SO "Description" left blank by default (Account + External Reference already carry customer/PO)
      account_id: p.account_code || "",
      seller_id: seller,
      expected_seller: su,  // customer's mapped sales org (for guardrail)
      release: true,
      distribution_channel: p.distribution_channel || defaults.distribution_channel || "01",
      employee_id: p.employee_id || defaults.employee_id || "",
      site_id: siteFromSalesUnit(su) || p.site_id || defaults.site_id || "",
      currency: p.currency || "USD",
      posting_date: p.posting_date || "",
      requested_date: p.requested_date || "",
      ship_to_party_id: "",
      // End Customer (stamped to SAP KUT fields). Direct order → the PO's own customer. Editable
      // below; transfer-party routing overwrites these with the resolved original customer.
      end_customer_code: p.app_customer_code || "",
      end_customer_name: p.customer_name || "",
      end_customer_po_ref: p.po_number || "",
      lines: (p.lines || []).map((l) => ({
        product_id: l.product_id || "", quantity: l.quantity || 0, unit_code: "EA",
        price: l.price ?? "", delivery_date: l.delivery_date || "",
      })),
    });
    // Transfer-party routing is NEVER applied automatically. On PO select we only surface the
    // customer's designated transfer party (if any) in the band — the Yes/No toggle defaults to
    // Direct. Routing applies ONLY when the user explicitly picks it (Yes / "Select party…").
    setTpRouting(null); setCustTp(null); setTpBillAddrs(null); setTpSource(null); setPushSteps(null);
    directSnapshot.current = { account_id: p.account_code || "", prices: (p.lines || []).map((l) => (l.price ?? "")) };
    if (p.app_customer_code) {
      api.get(`/dispatch/plan/addresses?pcode=${encodeURIComponent(p.app_customer_code)}&name=${encodeURIComponent(p.customer_name || "")}`)
        .then((r) => r.data || {})
        .catch(() => ({}))
        .then((d) => {
          setCustTp({ transfer_party: !!d.transfer_party, tp_code: d.tp_code, tp_name: d.tp_name });
        });
    }
    // Pull the real Sales Area (Sales Unit / Dist Channel / Ship-From) from this customer's recent
    // SAP Sales Orders — these override the generic customer-map/prefix defaults when SAP has history.
    setSalesArea(null);
    setMapSaving(false);
    setSoAutoFix(null);
    setVerify(null);
    setSiteTouched(false);
    setMapInfo({
      app_code: p.app_customer_code || "",
      account: p.account_code || "",
      mapped_su: p.sales_unit || "",
      mapped_dc: p.distribution_channel || "",
    });
    const prods = (p.lines || []).map((l) => l.product_id).filter(Boolean);
    if (p.account_code) {
      setSalesAreaLoading(true);
      const qp = new URLSearchParams({ account: p.account_code, products: prods.join(",") });
      api.get(`/sap/sales-area-suggestions?${qp.toString()}`)
        .then((r) => {
          const sa = r.data;
          setSalesArea(sa);
          if (!sa?.ok) return;
          setForm((f) => {
            if (!f || f.app_po_id !== p.id) return f;  // user switched PO meanwhile
            const sapSU = sa.sales_unit?.value || f.seller_id;
            // SAP history is the usual sales org, but line items still decide sellability — if the
            // SAP org can't sell every line, force the org that's valid for all items.
            const nsu = resolveSellerForLines(sapSU, p.lines);
            setSoAutoFix(nsu && sapSU && nsu !== sapSU ? { from: sapSU, to: nsu } : null);
            return {
              ...f,
              seller_id: nsu,
              expected_seller: sapSU || f.expected_seller,  // SAP is the source of truth
              distribution_channel: sa.distribution_channel?.value || f.distribution_channel,
              site_id: siteTouched ? f.site_id : (sa.ship_from_site?.value || f.site_id),
            };
          });
        })
        .catch(() => setSalesArea(null))
        .finally(() => setSalesAreaLoading(false));
    }
  };
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setForm((f) => ({ ...f, lines: f.lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));

  // Open the stored original PO document (PDF/image inline; RTF/DOC/TXT/EML as text). POs that
  // came from a bulk Excel/ERP sync have no per-PO document — we show a clear message for that.
  const viewFile = async () => {
    if (!sel?.id) return;
    setFileOpen(true); setFileState("loading");
    setFileUrl(""); setFileText(""); setFileName("");
    try {
      const r = await api.get(`/po/${sel.id}/file`, { responseType: "blob" });
      const m = r.data.type || "";
      setFileMime(m);
      const inlineOk = m.startsWith("image/") || m === "application/pdf";
      if (inlineOk) {
        setFileUrl(URL.createObjectURL(r.data));
      } else {
        try {
          const { data } = await api.get(`/po/${sel.id}/file-text`);
          setFileText(data.text || ""); setFileName(data.filename || "");
        } catch { /* fall through to generic view */ }
        setFileUrl(URL.createObjectURL(r.data));
      }
      setFileState("ok");
    } catch (e) {
      setFileState(e?.response?.status === 404 ? "none" : "error");
    }
  };

  const run = async (mode, sellerOverride) => {
    if (!form) return;
    if (!form.account_id) { toast.error("Enter the SAP Customer (Account) ID"); return; }
    if (!form.lines.length || form.lines.some((l) => !l.product_id)) { toast.error("Each line needs a SAP Product ID"); return; }
    // Transfer-party guardrail: a routed order never falls back to the PO price. Block the push
    // when AV MATICS isn't mapped to a SAP account, or any line has no valid approved transfer price.
    if (tpBlocked) {
      toast.error(tpRouting?.reason
        || `${tpRouting?.lines_blocked || 0} line(s) have no approved transfer price — set them in Settings › Transfer Pricing, or switch routing back to Direct.`);
      return;
    }
    const seller = sellerOverride || form.seller_id;
    if (sellerOverride) setF("seller_id", sellerOverride);
    setBusy(mode); setResult(null); setVerify(null);
    try {
      const shipAddr = shipSel || null;
      const billAddr = billSel || null;
      // Transfer-party routing (sold-to AV MATICS, drop-ship to end customer). End-Customer values
      // come from the editable form fields (synced from routing), so a manual edit always wins.
      const tpFields = tpActive ? {
        transfer_party: true,
        tp_code: tpRouting.tp_code,
        ship_to_party_id: tpRouting.end_customer_account || "",
      } : { transfer_party: false };
      // Step 1 writes the order WITHOUT auto-releasing — the user verifies the read-back first,
      // then explicitly releases (Step 2). So force release:false on the create call.
      const payload = { ...form, release: false, seller_id: seller, ship_to: shipAddr, bill_to: billAddr, ...tpFields, lines: form.lines.map((l) => ({ ...l, quantity: Number(l.quantity) || 0, price: l.price === "" || l.price == null ? null : Number(l.price) })) };
      const { data } = await api.post(`/sap/so/${mode}`, payload);
      setResult({ ...data, mode });
      if (data.blocked) {
        toast.error(data.message || "Blocked: product not sellable under the selected Sales Unit.");
      } else if (data.ok) {
        toast.success(mode === "check"
          ? "SAP check passed — no errors (nothing created)"
          : `Created SAP Sales Order ${data.order_id || ""} — verify below, then release`);
        if (mode === "push") {
          search(q);
          loadHistory();
          if (sel) setSel({ ...sel, sap_so_id: data.order_id, sap_so_uuid: data.order_uuid });
          if (data.order_id) loadVerify(data.order_id);
        }
      } else {
        toast.error(mode === "check"
          ? "SAP check found errors — order would be rejected. See details."
          : "SAP rejected the order — nothing was created. See details.");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Request failed"); }
    finally { setBusy(""); }
  };

  // Build the push payload (shared by dry-run and the streaming push).
  const buildPayload = (seller) => {
    const shipAddr = shipSel || null;
    const billAddr = billSel || null;
    const tpFields = tpActive ? {
      transfer_party: true, tp_code: tpRouting.tp_code,
      ship_to_party_id: tpRouting.end_customer_account || "",
    } : { transfer_party: false };
    return { ...form, release: false, seller_id: seller, ship_to: shipAddr, bill_to: billAddr, ...tpFields,
      lines: form.lines.map((l) => ({ ...l, quantity: Number(l.quantity) || 0, price: l.price === "" || l.price == null ? null : Number(l.price) })) };
  };

  // "Create & Write to SAP" — RELIABLE async job + polling. Starts a background job (returns a
  // job_id instantly), then polls for progress so a slow SAP commit can never trip the proxy /
  // 502. Keeps the same live progress bar (validate → create → stamp → verify → done).
  const runStream = async () => {
    if (!form) return;
    if (!form.account_id) { toast.error("Enter the SAP Customer (Account) ID"); return; }
    if (!form.lines.length || form.lines.some((l) => !l.product_id)) { toast.error("Each line needs a SAP Product ID"); return; }
    if (tpBlocked) {
      toast.error(tpRouting?.reason || `${tpRouting?.lines_blocked || 0} line(s) have no approved transfer price — set them in Settings › Transfer Pricing, or switch routing back to Direct.`);
      return;
    }
    const seller = form.seller_id;
    setBusy("push"); setResult(null); setVerify(null);
    // Elapsed timer — SAP write-commit latency varies (~20–90s); show users it's still working.
    setPushElapsed(0);
    if (pushTimerRef.current) clearInterval(pushTimerRef.current);
    const startedAt = Date.now();
    pushTimerRef.current = setInterval(() => setPushElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    const init = {};
    PUSH_STEPS.forEach((s) => { init[s.key] = { status: "pending", label: s.label }; });
    init.validate.status = "run";
    setPushSteps(init);
    const applyMilestones = (milestones) => {
      if (!milestones || !milestones.length) return;
      setPushSteps((prev) => {
        const next = { ...(prev || {}) };
        for (const ev of milestones) {
          if (ev.type !== "milestone") continue;
          next[ev.step] = { status: ev.status, label: ev.label || next[ev.step]?.label, detail: ev };
        }
        return next;
      });
    };
    const finalize = (finalRes) => {
      if (!finalRes) return;
      setResult({ ...finalRes, mode: "push" });
      if (finalRes.blocked) {
        toast.error(finalRes.message || "Blocked: product not sellable under the selected Sales Unit.");
      } else if (finalRes.ok) {
        toast.success(finalRes.recovered_via_readback
          ? `SAP Sales Order ${finalRes.order_id || ""} confirmed — recovered directly from SAP after a slow commit`
          : `Created SAP Sales Order ${finalRes.order_id || ""} — verified above`);
        search(q);
        loadHistory();
        if (sel) setSel({ ...sel, sap_so_id: finalRes.order_id, sap_so_uuid: finalRes.order_uuid });
        if (finalRes.order_id) loadVerify(finalRes.order_id);
      } else {
        toast.error(finalRes.reason || "SAP rejected the order — nothing was created. See details.");
      }
    };
    const headers = { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("jde_token")}` };
    // Pre-push duplicate guard — SAP doesn't enforce ExternalReference uniqueness, so warn if a
    // Sales Order already exists for this PO before creating another.
    try {
      const extRef = buildPayload(seller).external_id;
      if (extRef) {
        const ck = await fetch(`${API_BASE}/sap/so/exists?external_ref=${encodeURIComponent(extRef)}`, { headers });
        if (ck.ok) {
          const cj = await ck.json();
          if (cj.exists && !window.confirm(
            `⚠ A Sales Order (#${cj.order_id}) already exists in SAP for PO reference "${extRef}".\n\n` +
            `Creating another will DUPLICATE it. Continue anyway?`)) {
            setBusy("");
            if (pushTimerRef.current) { clearInterval(pushTimerRef.current); pushTimerRef.current = null; }
            setPushSteps(null);
            toast.info(`Push cancelled — SAP SO #${cj.order_id} already exists for this PO.`);
            return;
          }
        }
      }
    } catch { /* check is best-effort; never block the push on a guard error */ }
    try {
      // 1) Start the background job (instant).
      const startResp = await fetch(`${API_BASE}/sap/so/push-async`, {
        method: "POST", headers, body: JSON.stringify(buildPayload(seller)),
      });
      if (!startResp.ok) throw new Error(`HTTP ${startResp.status}`);
      const { job_id } = await startResp.json();
      if (!job_id) throw new Error("no job id");
      // 2) Poll for progress until the job finishes (no held-open connection → no 502).
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      for (let i = 0; i < 180; i++) {   // up to ~6 min
        await sleep(2000);
        let job;
        try {
          const jr = await fetch(`${API_BASE}/sap/so/push-job/${job_id}`, { headers });
          if (!jr.ok) continue;         // transient — keep polling
          job = await jr.json();
        } catch { continue; }
        applyMilestones(job.milestones);
        if (job.status === "running") continue;
        if (job.status === "stale") {
          toast.error("Lost contact with the push worker — check SAP / Progress History before retrying.");
          setPushSteps((prev) => { if (!prev) return prev; const n = { ...prev }; for (const k of Object.keys(n)) if (n[k].status === "run") n[k] = { ...n[k], status: "warn" }; return n; });
          break;
        }
        finalize(job.result);           // done | failed | error
        break;
      }
    } catch (e) {
      toast.error("Push failed — " + (e?.message || "connection error"));
      setPushSteps((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        for (const k of Object.keys(next)) if (next[k].status === "run") next[k] = { ...next[k], status: "error" };
        return next;
      });
    } finally { setBusy(""); if (pushTimerRef.current) { clearInterval(pushTimerRef.current); pushTimerRef.current = null; } }
  };

  const warnNotes = (result?.notes || []).filter((n) => !(result?.error_notes || []).includes(n));

  // Step 2 of the robust push — read the created order back from SAP (so the user sees exactly
  // what's written: Sales Order Date, Business Residence, Posting Date, Sales Org, line qtys), then
  // release it only when every mandatory field is present & the order is Consistent.
  const loadVerify = async (orderId) => {
    const oid = String(orderId || "").trim();
    if (!oid) return;
    setVerifying(true);
    try {
      const { data } = await api.get(`/sap/so/${encodeURIComponent(oid)}/details`);
      setVerify(data);
    } catch (e) {
      setVerify({ ok: false, reason: e?.response?.data?.detail || "Could not read the order back from SAP" });
    } finally { setVerifying(false); }
  };
  const doRelease = async (orderId) => {
    const oid = String(orderId || "").trim();
    if (!oid) return;
    setReleasing(true);
    try {
      const { data } = await api.post(`/sap/so/${encodeURIComponent(oid)}/release`, {
        so_date: form?.posting_date || "",
        business_residence: (form?.site_id || "").trim(),
        app_po_id: form?.app_po_id || null,
        release: true,
      });
      if (data.details) setVerify(data.details);
      if (data.ok && data.details?.released) {
        toast.success(`SAP SO #${oid} released — now ${data.details?.lifecycle || "In Process"}`);
      } else {
        toast.error(`Release did not complete: ${data.odata_release?.message || data.odata_release?.reason || "order may still be inconsistent"}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Release request failed"); }
    finally { setReleasing(false); }
  };

  // Re-stamp Sales Order Date + Business Residence on an already-created SAP order that got
  // stuck "In Preparation" (the post-create OData PATCH failed at push time). Idempotent —
  // no SOAP create, so it can never duplicate the order.
  const [fixing, setFixing] = useState(false);
  const fixHeader = async (orderId) => {
    const oid = String(orderId || "").trim();
    if (!oid) { toast.error("No SAP Sales Order # to fix"); return; }
    const site = (form?.site_id || "").trim();
    if (!site) { toast.error("Set the Ship-From Site first (it's the Business Residence)"); return; }
    setFixing(true);
    try {
      const { data } = await api.post(`/sap/so/${encodeURIComponent(oid)}/fix-header`, {
        business_residence: site,
        so_date: form?.posting_date || "",
        release: !!form?.release,
        app_po_id: form?.app_po_id || null,
      });
      if (data.ok) {
        toast.success(`SAP SO #${oid} header fixed — Date & Business Residence set${data.odata_release?.ok ? " · released" : ""}`);
        setResult((r) => (r ? { ...r, odata_header_update: data.odata_header_update, odata_release: data.odata_release } : r));
      } else {
        toast.error(`Fix failed: ${data.odata_header_update?.reason || data.odata_header_update?.message || "see SAP admin note"}`);
        setResult((r) => (r ? { ...r, odata_header_update: data.odata_header_update } : r));
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Fix-header request failed"); }
    finally { setFixing(false); }
  };

  // Re-verify & re-stamp the custom header + End Customer KUT fields on an already-created SAP
  // order — the manual safety net when the push-time Verify ended ⚠ (ByD swallowed a KUT write in
  // its async extension store). Idempotent: PATCH + read-back only, never a SOAP create.
  const [restamping, setRestamping] = useState(false);
  const restampNow = async (orderId) => {
    const oid = String(orderId || result?.order_id || "").trim();
    if (!oid) { toast.error("No SAP Sales Order # to re-stamp"); return; }
    setRestamping(true);
    setPushSteps((prev) => (prev ? { ...prev, stamp: { ...prev.stamp, status: "run" }, verify: { ...prev.verify, status: "run" } } : prev));
    try {
      const { data } = await api.post(`/sap/so/${encodeURIComponent(oid)}/restamp`, {
        so_date: form?.posting_date || "",
        business_residence: (form?.site_id || "").trim(),
        end_customer_code: form?.end_customer_code || result?.end_customer?.code || "",
        end_customer_name: form?.end_customer_name || result?.end_customer?.name || "",
        end_customer_po_ref: form?.end_customer_po_ref || result?.end_customer?.po_ref || "",
        app_po_id: form?.app_po_id || null,
      });
      const blanks = data.header_verify?.still_blank || [];
      setResult((r) => (r ? { ...r, header_verify: data.header_verify, odata_header_update: data.odata_header_update, end_customer: data.end_customer || r.end_customer } : r));
      setPushSteps((prev) => (prev ? {
        ...prev,
        stamp: { ...prev.stamp, status: data.ok ? "done" : "warn" },
        verify: { ...prev.verify, status: data.ok ? "done" : "warn", detail: { ...(prev.verify?.detail || {}), attempts: data.header_verify?.attempts, still_blank: blanks } },
      } : prev));
      if (data.ok) toast.success("Re-stamped — End Customer fields verified in SAP");
      else toast.error(data.header_verify?.not_editable
        ? (data.header_verify.reason || "Order is past 'In Preparation' — reopen it to write these fields")
        : `Still not confirmed${blanks.length ? ` — blank: ${blanks.join(", ")}` : ""}`);
      if (oid) loadVerify(oid);
    } catch (e) { toast.error(e?.response?.data?.detail || "Re-stamp request failed"); }
    finally { setRestamping(false); }
  };

  // Pin / unpin the current Bill-To as the party's (AV MATICS) default, so future routed pushes
  // reuse it instead of defaulting to a branch address.
  const pinBillTo = async () => {
    const party = tpRouting?.tp_code;
    if (!party) return;
    if (!billSel) { toast.error("Pick a Bill-To address first"); return; }
    setPinning(true);
    try {
      await api.put(`/sap/so/bill-to-default/${encodeURIComponent(party)}`, { label: tpRouting?.tp_name || party, address: billSel });
      setTpBillPinned(billSel);
      toast.success(`Pinned as ${tpRouting?.tp_name || party}'s default Bill-To`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not pin Bill-To"); }
    finally { setPinning(false); }
  };
  const unpinBillTo = async () => {
    const party = tpRouting?.tp_code;
    if (!party) return;
    setPinning(true);
    try {
      await api.delete(`/sap/so/bill-to-default/${encodeURIComponent(party)}`);
      setTpBillPinned(null);
      toast.success("Default Bill-To unpinned");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not unpin Bill-To"); }
    finally { setPinning(false); }
  };
  // True when the current Bill-To selection matches the pinned default (stringified compare).
  const billIsPinned = !!(tpBillPinned && billSel && JSON.stringify(tpBillPinned) === JSON.stringify(billSel));

  // Progress History: recent SAP pushes + their milestone timelines (global, newest first).
  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/sap/so/push-history?limit=40`);
      setHistory(data || []);
    } catch { setHistory([]); }
    finally { setHistoryLoading(false); }
  };
  useEffect(() => { loadHistory(); }, []);


  // Detect "financial data / valuation incomplete" messages and pull out product + company so the
  // user can activate the item valuation in SAP without leaving the app.
  const valuationIssues = (() => {
    const lines = [...(result?.error_notes || []), ...warnNotes, ...(result?.notes || [])];
    const found = {};
    const res = [
      /product\s+([A-Za-z0-9._\-]+)\s+incomplete\s+for\s+company\s+([A-Za-z0-9]+)/i,
      /no\s+inventory\s+cost\s+for\s+(?:material|product)\s+([A-Za-z0-9._\-]+)\s+in\s+company\s+([A-Za-z0-9]+)/i,
    ];
    for (const n of lines) {
      for (const re of res) {
        const m = re.exec(n || "");
        if (m) { const k = `${m[1]}|${m[2]}`; if (!found[k]) found[k] = { product: m[1], company: m[2] }; }
      }
    }
    return Object.values(found);
  })();

  const [activating, setActivating] = useState("");
  const activateValuation = async (product, company) => {
    if (!form?.site_id) { toast.error("Set the Ship-From Site first (it's the valuation Business Residence)"); return; }
    if (!window.confirm(`Activate valuation in SAP for ${product} (company ${company}, site ${form.site_id})?\n\nThis WRITES to live SAP: inventory cost 0 + Account Determination Group 3030, then activates the Valuation view. There is no dry-run for this service.`)) return;
    setActivating(`${product}|${company}`);
    try {
      const { data } = await api.post("/sap/material/activate-valuation", {
        product_id: product, company_id: company, business_residence: form.site_id,
        adg_code: "3030", po_date: form.posting_date || form.requested_date || "", cost: 0, check: false,
      });
      if (data.ok) toast.success(`Valuation activated for ${product}. Re-run Dry-run Check to confirm.`);
      else toast.error(`Activation failed: ${(data.steps || []).map((s) => (s.notes || []).join("; ")).filter(Boolean).join(" | ") || data.reason || "see logs"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Activation request failed"); }
    finally { setActivating(""); }
  };

  return (
    <Layout breadcrumb={["Setup", "SAP Push"]}>
      <div data-testid="sap-push-page" className="jde-content" style={{ paddingTop: 8 }}>
        {/* Page header */}
        <div className="sp-header">
          <div>
            <h1 className="sp-title"><CloudArrowUp size={22} weight="bold" color="var(--omh-primary)" /> SAP Push — Sales Orders</h1>
            <p className="sp-sub">
              Create a customer Sales Order in SAP Business ByDesign from a customer PO. Always run
              {" "}<b>Dry-run Check</b> first — it validates in SAP without creating anything.{" "}
              <a href="/sap-mapping" data-testid="sap-push-mapping-link"><LinkSimple size={13} weight="bold" /> Manage SAP Mapping</a>
            </p>
          </div>
          <div
            data-testid="sap-conn-banner"
            className={"sp-conn " + (conn == null ? "pending" : conn.ok ? "ok" : "bad")}
          >
            {conn == null ? <Info size={16} weight="fill" />
              : conn.ok ? <CheckCircle size={16} weight="fill" color="#059669" />
                : <XCircle size={16} weight="fill" color="#DC2626" />}
            <span>
              {conn == null ? "Checking SAP connection…"
                : conn.ok ? <>Connected · <b>{conn.tenant}</b> · {conn.user} · {conn.services_discovered} services</>
                  : <>SAP not reachable: {conn.reason || conn.http_status}</>}
            </span>
          </div>
        </div>

        {/* Split-app: master list + detail object page */}
        <div className="sp-split">
          {/* MASTER LIST */}
          <div className="jde-panel sp-master">
            <div className="sp-search">
              <MagnifyingGlass size={15} weight="bold" color="var(--omh-text-soft)" style={{ position: "absolute", left: 22, top: "50%", transform: "translateY(-50%)" }} />
              <input data-testid="sap-search" className="jde-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer PO / customer…" />
            </div>
            {SAP_PUSH_FILTERS_DRAFT && (
            <div style={{ padding: "0 12px 10px", display: "flex", flexDirection: "column", gap: 8 }}>
              <select data-testid="sap-filter-customer" className="jde-input" value={custFilter} onChange={(e) => setCustFilter(e.target.value)}>
                <option value="">All customers</option>
                {poCustomers.map((c) => (
                  <option key={c.code} value={c.code}>{c.code}{c.name && c.name !== c.code ? ` · ${c.name}` : ""}</option>
                ))}
              </select>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <div style={{ flex: 1, minWidth: 0 }}><DateField testid="sap-filter-date-from" value={dateFrom} onChange={setDateFrom} placeholder="From (dd-mmm-yy)" /></div>
                <span style={{ color: "var(--omh-text-soft)", fontSize: 12 }}>→</span>
                <div style={{ flex: 1, minWidth: 0 }}><DateField testid="sap-filter-date-to" value={dateTo} onChange={setDateTo} placeholder="To (dd-mmm-yy)" /></div>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <label data-testid="sap-filter-unpushed" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "var(--omh-text)", cursor: "pointer" }}>
                  <input type="checkbox" checked={unpushed} onChange={(e) => setUnpushed(e.target.checked)} />
                  Open only (not yet pushed)
                </label>
                {activeFilters > 0 && (
                  <button data-testid="sap-filter-clear" onClick={clearFilters} style={{ border: "none", background: "none", color: "var(--omh-primary,#2A5C9B)", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    Clear ({activeFilters})
                  </button>
                )}
              </div>
            </div>
            )}
            <div data-testid="sap-po-count" style={{ padding: "0 12px 6px", fontSize: 11.5, color: "var(--omh-text-soft)", fontWeight: 600 }}>
              {pos.length} PO{pos.length === 1 ? "" : "s"}
              {pos.length >= 500 && <span style={{ color: "var(--omh-warn,#b45309)" }}> · showing first 500 — narrow the date / customer filter to see the rest</span>}
            </div>
            <div className="sp-list">
              {pos.map((p) => {
                const todayISO = new Date().toISOString().slice(0, 10);
                const reqPast = p.requested_date && String(p.requested_date).slice(0, 10) < todayISO;
                return (
                <button
                  key={p.id}
                  data-testid={`sap-po-${p.id}`}
                  onClick={() => pick(p)}
                  className={"sp-item" + (sel?.id === p.id ? " active" : "")}
                >
                  <div className="sp-item-row">
                    <span className="sp-item-title">{p.po_number || "—"}</span>
                    {p.sap_so_id && <span data-testid={`sap-po-soref-${p.id}`} className="sp-soref">SAP SO #{p.sap_so_id}</span>}
                    {reqPast && (
                      <span data-testid={`sap-po-reqpast-${p.id}`} title="Requested delivery date is in the past — SAP will push it to a future date. Fix it before pushing."
                        style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, fontWeight: 700, color: "#b45309", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 999, padding: "1px 7px", marginLeft: "auto" }}>
                        <Warning size={11} weight="fill" /> Past req date
                      </span>
                    )}
                  </div>
                  <div className="sp-item-sub">{p.customer_name} · {p.line_count} lines · {p.currency}</div>
                  {(p.posting_date || p.requested_date) && (
                    <div className="sp-item-sub" data-testid={`sap-po-dates-${p.id}`} style={{ fontSize: 10.5, opacity: 0.85 }}>
                      PO {p.posting_date ? fmtDMY(p.posting_date) : "—"} · <span style={reqPast ? { color: "#b45309", fontWeight: 700, opacity: 1 } : undefined}>Req {p.requested_date ? fmtDMY(p.requested_date) : "—"}</span>
                    </div>
                  )}
                </button>
                );
              })}
              {pos.length === 0 && <div className="sp-empty" style={{ padding: 30, fontSize: 12 }}>No POs found.</div>}
            </div>
          </div>

          {/* DETAIL OBJECT PAGE */}
          <div className="jde-panel sp-detail">
            {!form ? (
              <div className="sp-empty" style={{ display: "block", padding: 24 }}>
                <div style={{ textAlign: "center", marginBottom: 20 }}>
                  <Package size={30} weight="duotone" color="var(--omh-text-soft)" style={{ marginBottom: 10 }} />
                  <div>Select a customer PO on the left to map &amp; create a Sales Order in SAP.</div>
                </div>
                <PushHistoryPanel history={history} historyOpen={historyOpen} setHistoryOpen={setHistoryOpen} historyLoading={historyLoading} loadHistory={loadHistory} historyExpanded={historyExpanded} setHistoryExpanded={setHistoryExpanded} />
              </div>
            ) : (
              <>
                {/* Object header */}
                <div className="sp-objhdr">
                  <div className="sp-objhdr-top">
                    <div>
                      <h2 className="sp-objtitle">{sel?.po_number || form.external_id || "New Sales Order"}</h2>
                      <div className="sp-objsub">{sel?.customer_name || "—"}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button data-testid="sap-view-po-file" className="jde-btn" onClick={viewFile}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <FileText size={14} weight="bold" /> View PO file
                      </button>
                      <span className={"sp-objstatus " + (sel?.sap_so_id ? "done" : "todo")}>
                        {sel?.sap_so_id ? <><CheckCircle size={14} weight="fill" /> Pushed · SO #{sel.sap_so_id}</> : <><Info size={14} weight="fill" /> Not pushed</>}
                      </span>
                    </div>
                  </div>
                  <div className="sp-kv">
                    <div><div className="k">Customer</div><div className="v">{sel?.customer_name || "—"}</div></div>
                    <div><div className="k">Account ID</div><div className="v">{form.account_id || "—"}</div></div>
                    <div><div className="k">Lines</div><div className="v">{form.lines.length}</div></div>
                    <div><div className="k">Currency</div><div className="v">{form.currency || "—"}</div></div>
                    {sel?.sap_so_uuid && <div><div className="k">SO UUID</div><div className="v mono">{sel.sap_so_uuid}</div></div>}
                  </div>
                </div>

                {/* Scrollable form body */}
                <div className="sp-body">
                  {(custTp?.transfer_party || tpOptions.length > 0) && (
                    <div data-testid="sap-tp-band" style={{
                      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                      marginBottom: 12, padding: "9px 12px", borderRadius: 8,
                      border: "1px solid " + (tpActive ? "#0F766E" : "var(--omh-border,#e2e8f0)"),
                      background: tpActive ? "#F0FDFA" : "#F9FAFB",
                    }}>
                      <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "#6B7280" }}>Transfer Pricing</span>
                      {custTp?.transfer_party ? (
                        <>
                          <span data-testid="sap-tp-name" style={{ fontSize: 12.5, color: tpActive ? "#0F766E" : "#9CA3AF", fontWeight: 700, textDecoration: tpActive ? "none" : "line-through" }}>
                            {tpRouting?.tp_name || custTp.tp_name || custTp.tp_code} <span style={{ fontWeight: 500, color: "#9CA3AF" }}>({tpRouting?.tp_code || custTp.tp_code})</span>
                            {tpSrcChip}
                          </span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#6B7280" }}>· ordered by {sel?.customer_name || "—"}</span>
                          <span style={{ flex: 1 }} />
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#6B7280" }}>Route via transfer party?</span>
                          <div style={{ display: "flex", border: "1px solid var(--omh-border,#e2e8f0)", borderRadius: 999, overflow: "hidden" }}>
                            <span data-testid="sap-tp-no" onClick={chooseDirect} style={{ padding: "4px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", background: !tpActive ? "#6B7280" : "white", color: !tpActive ? "white" : "#6B7280" }}>No</span>
                            <span data-testid="sap-tp-yes" onClick={() => !tpActive && chooseTp(custTp.tp_code, custTp.tp_name)} style={{ padding: "4px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", background: tpActive ? "#0F766E" : "white", color: tpActive ? "white" : "#6B7280" }}>{tpLoading ? "…" : "Yes"}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          {tpActive ? (
                            <span data-testid="sap-tp-name" style={{ fontSize: 12.5, color: "#0F766E", fontWeight: 700 }}>
                              {tpRouting.tp_name} <span style={{ fontWeight: 500, color: "#9CA3AF" }}>({tpRouting.tp_code})</span>
                              {tpSrcChip}
                            </span>
                          ) : (
                            <span style={{ fontSize: 12.5, color: "#6B7280" }}>Direct to {sel?.customer_name || "customer"} at PO prices</span>
                          )}
                          <span style={{ flex: 1 }} />
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#6B7280" }}>Route via transfer party?</span>
                          {tpActive ? (
                            <span data-testid="sap-tp-clear" onClick={chooseDirect} title="Remove transfer-party routing" style={{ cursor: "pointer", color: "#9CA3AF", fontWeight: 800, fontSize: 16, lineHeight: 1 }}>×</span>
                          ) : (
                            <select data-testid="sap-tp-manual-select" className="jde-select" style={{ minWidth: 150, fontSize: 11 }} value=""
                              disabled={tpLoading}
                              onChange={(e) => { if (e.target.value) { const t = tpOptions.find((x) => x.tp_code === e.target.value); chooseTp(e.target.value, t?.tp_name); } }}>
                              <option value="">{tpLoading ? "Routing…" : "Select party…"}</option>
                              {tpOptions.map((t) => <option key={t.tp_code} value={t.tp_code}>{t.tp_name || t.tp_code}</option>)}
                            </select>
                          )}
                        </>
                      )}
                      {tpActive && !tpBlocked && (
                        <span data-testid="sap-tp-note" style={{ fontSize: 11, width: "100%", color: "#0F766E", fontWeight: 600 }}>
                          Sold to {tpRouting.tp_name} · End customer: {tpRouting.end_customer?.name || tpRouting.end_customer?.code} · Transfer pricing{tpRouting.end_customer_account ? " · drop-ship to end customer" : ""}.
                          {tpRouting.account_source === "sap" && (
                            <span style={{ color: "#92400E", fontWeight: 500 }}> {" "}Account resolved from SAP — map {tpRouting.tp_code} on the SAP Mapping page to pin its sales org.</span>
                          )}
                        </span>
                      )}
                      {tpActive && !tpBlocked && tpHighVar > 0 && (
                        <span data-testid="sap-tp-variance-warn" style={{ fontSize: 11, width: "100%", color: "#92400E", fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
                          <Warning size={12} weight="fill" />
                          {tpHighVar} line{tpHighVar > 1 ? "s" : ""} deviate more than {TP_VAR_THRESHOLD}% from the PO price — please double-check the transfer rates before pushing.
                        </span>
                      )}
                      {tpBlocked && (
                        <span data-testid="sap-tp-blocked" style={{ fontSize: 11, width: "100%", color: "#B91C1C", fontWeight: 600 }}>
                          <Warning size={12} weight="fill" style={{ verticalAlign: -2, marginRight: 3 }} />
                          {tpRouting.reason || `${tpRouting.lines_blocked} line(s) have no approved transfer price — set them in Settings › Transfer Pricing, or switch back to Direct. The push is blocked until then.`}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Live "Push to SAP" progress — a tick per milestone (incl. read-back verify) */}
                  {pushSteps && (busy === "push" || Object.values(pushSteps).some((v) => v.status === "error" || v.status === "warn")) && (
                    <div data-testid="sap-push-progress" style={{ marginBottom: 12, padding: "12px 14px", borderRadius: 10, border: "1px solid var(--omh-border,#e2e8f0)", background: "#0B1220", color: "#E5E7EB" }}>
                      {(() => {
                        const steps = PUSH_STEPS.filter((s) => pushSteps[s.key]);
                        const doneCount = steps.filter((s) => pushSteps[s.key]?.status === "done").length;
                        const pct = steps.length ? Math.round((doneCount / steps.length) * 100) : 0;
                        const anyErr = Object.values(pushSteps).some((v) => v.status === "error");
                        return (
                          <>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                              <CloudArrowUp size={16} weight="fill" color="#38BDF8" />
                              <span style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: 0.3 }}>Pushing to SAP</span>
                              <span data-testid="sap-push-timer" style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", fontFamily: "monospace", fontVariantNumeric: "tabular-nums" }}>
                                {String(Math.floor(pushElapsed / 60)).padStart(1, "0")}:{String(pushElapsed % 60).padStart(2, "0")}
                              </span>
                              <span data-testid="sap-push-progress-pct" style={{ marginLeft: "auto", fontSize: 11.5, fontWeight: 700, color: anyErr ? "#FCA5A5" : "#7DD3FC" }}>{pct}%</span>
                            </div>
                            <div style={{ height: 6, borderRadius: 99, background: "rgba(255,255,255,0.08)", overflow: "hidden", marginBottom: 12 }}>
                              <div style={{ height: "100%", width: `${pct}%`, background: anyErr ? "#EF4444" : "linear-gradient(90deg,#0EA5E9,#22D3EE)", borderRadius: 99, transition: "width .35s ease" }} />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                              {PUSH_STEPS.map((s) => {
                                const st = pushSteps[s.key];
                                if (!st) return null;
                                const status = st.status;
                                const icon = status === "done" ? <CheckCircle size={17} weight="fill" color="#22C55E" />
                                  : status === "error" ? <XCircle size={17} weight="fill" color="#EF4444" />
                                  : status === "warn" ? <Warning size={17} weight="fill" color="#F59E0B" />
                                  : status === "run" ? <CircleNotch size={17} weight="bold" color="#38BDF8" className="spin" />
                                  : <Circle size={17} weight="bold" color="#475569" />;
                                const blanks = (st.detail?.still_blank || []);
                                return (
                                  <div key={s.key} data-testid={`sap-push-step-${s.key}`} data-status={status} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, opacity: status === "pending" ? 0.5 : 1 }}>
                                    {icon}
                                    <span style={{ fontWeight: status === "run" ? 700 : 500 }}>{st.label}</span>
                                    {s.key === "verify" && status === "done" && st.detail?.attempts > 1 && (
                                      <span style={{ fontSize: 10.5, color: "#FCD34D" }}>re-stamped ×{st.detail.attempts}</span>
                                    )}
                                    {s.key === "create" && st.detail?.order_id && (
                                      <span style={{ fontSize: 10.5, color: "#93C5FD", fontFamily: "monospace" }}>#{st.detail.order_id}</span>
                                    )}
                                    {s.key === "create" && status === "run" && (
                                      <span style={{ fontSize: 10.5, color: pushElapsed > 90 ? "#FCD34D" : "#64748B" }}>
                                        {pushElapsed > 90
                                          ? `SAP still committing — ${pushElapsed}s elapsed. Large orders can take longer; we verify directly with SAP, so this won't be lost…`
                                          : `SAP is committing — usually 20–90s (${pushElapsed}s)…`}
                                      </span>
                                    )}
                                    {status === "warn" && blanks.length > 0 && (
                                      <span style={{ fontSize: 10.5, color: "#FCA5A5" }}>still blank: {blanks.join(", ")}</span>
                                    )}
                                    {status === "error" && st.detail?.message && (
                                      <span style={{ fontSize: 10.5, color: "#FCA5A5" }}>{st.detail.message}</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            {(pushSteps.verify?.status === "warn" || pushSteps.stamp?.status === "warn") && (result?.order_id) && (
                              <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                <Warning size={15} weight="fill" color="#F59E0B" />
                                <span style={{ fontSize: 11.5, color: "#FCD34D" }}>
                                  Some End Customer fields didn't confirm. Re-run the stamp &amp; read-back.
                                </span>
                                <button data-testid="sap-push-restamp-btn" className="jde-btn jde-btn-primary" disabled={restamping}
                                  style={{ marginLeft: "auto", fontSize: 12 }}
                                  onClick={() => restampNow(result?.order_id)}>
                                  {restamping ? "Re-stamping…" : "Re-verify & re-stamp now"}
                                </button>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {/* Sales-org guard: hard block (with optional one-click Sales Unit switch) */}
                  {result?.blocked && (
                    <div data-testid="sap-so-blocked" className="sp-strip error" style={{ display: "block" }}>
                      <div className="sp-strip-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <XCircle size={18} weight="fill" /> Push blocked — Sales Unit mismatch
                      </div>
                      <div style={{ marginTop: 6 }}>{result.message}</div>
                      <div style={{ marginTop: 8, fontSize: 12.5 }}>
                        {(result.violations || []).slice(0, 12).map((v, i) => (
                          <div key={i} data-testid={`sap-so-violation-${i}`} style={{ padding: "2px 0" }}>
                            • <b>{v.product_id}</b> sells under: {(v.sales_orgs || []).join(", ")}
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {(result.suggested_sales_units || []).map((u) => (
                          <button key={u} data-testid={`sap-so-switch-${u}`} className="jde-btn jde-btn-primary" disabled={!!busy}
                            onClick={() => run(result.mode || "push", u)}>
                            Switch to {u} &amp; {result.mode === "check" ? "re-check" : "push"}
                          </button>
                        ))}
                        {(!result.suggested_sales_units || result.suggested_sales_units.length === 0) && (
                          <span style={{ fontSize: 12.5, opacity: 0.9 }}>No single Sales Unit covers every line — split the order or adjust products.</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Result message strip */}
                  {result && !result.blocked && (
                    <div data-testid="sap-result" className={"sp-strip " + (result.ok ? "success" : "error")}>
                      {result.ok ? <CheckCircle size={18} weight="fill" style={{ flexShrink: 0, marginTop: 1 }} />
                        : <XCircle size={18} weight="fill" style={{ flexShrink: 0, marginTop: 1 }} />}
                      <div style={{ flex: 1 }}>
                        <div className="sp-strip-title">
                          {result.ok
                            ? (result.mode === "check" ? "Check passed — no errors (nothing created)" : `Created SAP Sales Order #${result.order_id || "?"}`)
                            : (result.mode === "check" ? "Check found errors — SAP would reject this order" : "SAP rejected the order — nothing was created")}
                        </div>
                        {result.ok && result.mode !== "check" && result.order_uuid && (
                          <div data-testid="sap-result-soref" className="sp-strip-mono" style={{ marginTop: 4 }}>SO Reference: {result.order_id} · UUID {result.order_uuid}</div>
                        )}
                        {result.fault && <div style={{ marginTop: 4 }}>{result.fault}</div>}
                        {(result.error_notes || []).map((n, i) => (
                          <div key={`e${i}`} data-testid={`sap-result-error-${i}`} className="sp-strip-err-line" style={{ marginTop: 4 }}>• {n}</div>
                        ))}
                        {result.transaction_id && <div style={{ marginTop: 6, opacity: 0.85 }}>SAP Transaction ID: <b>{result.transaction_id}</b> (open Web-Service Message Monitoring for full detail)</div>}
                      </div>
                    </div>
                  )}

                  {/* Step 2 · SAP read-back — show exactly what's written, then release */}
                  {(verify || verifying) && result?.mode !== "check" && (
                    <div data-testid="sap-verify-panel" className="sp-strip" style={{ marginTop: 10, display: "block", background: "#F8FAFC", border: "1px solid #CBD5E1" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                        <div className="sp-strip-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Info size={16} weight="fill" /> Step 2 · Verify what's written in SAP{verify?.order_id ? ` · SO #${verify.order_id}` : ""}
                        </div>
                        <button data-testid="sap-verify-reload" className="jde-btn" disabled={verifying}
                          onClick={() => loadVerify(verify?.order_id || result?.order_id)}>
                          {verifying ? "Reading…" : "Re-read from SAP"}
                        </button>
                      </div>
                      {verify && !verify.ok && (
                        <div className="sp-strip-err-line" style={{ marginTop: 8 }}>• {verify.reason || "Could not read the order from SAP"}</div>
                      )}
                      {verify && verify.ok && (
                        <>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 10, fontSize: 13 }}>
                            <span>Lifecycle: <b data-testid="sap-verify-lifecycle">{verify.lifecycle || "—"}</b></span>
                            <span>Release: <b style={{ color: verify.released ? "#15803d" : "#b45309" }} data-testid="sap-verify-release-status">{verify.release_status || "—"}</b></span>
                            <span>Consistency: <b style={{ color: (verify.consistency || "").toLowerCase() === "consistent" ? "#15803d" : "#b45309" }}>{verify.consistency || "—"}</b></span>
                          </div>
                          <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 8 }}>
                            {[["Sales Order Date", verify.mandatory?.sales_order_date], ["Business Residence", verify.mandatory?.business_residence], ["Consistency", verify.mandatory?.consistent]].map(([label, m]) => (
                              <div key={label} data-testid={`sap-verify-chk-${label.replace(/\s+/g, "-").toLowerCase()}`}
                                style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 6, background: m?.ok ? "#ECFDF5" : "#FEF2F2", border: `1px solid ${m?.ok ? "#A7F3D0" : "#FECACA"}` }}>
                                {m?.ok ? <CheckCircle size={16} weight="fill" style={{ color: "#15803d", flexShrink: 0 }} /> : <XCircle size={16} weight="fill" style={{ color: "#dc2626", flexShrink: 0 }} />}
                                <span style={{ fontSize: 12.5 }}><b>{label}:</b> {m?.value || (m?.ok ? "set" : "missing")}</span>
                              </div>
                            ))}
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 10, fontSize: 12.5, color: "#475569" }}>
                            <span>SAP SO #: <b>{verify.sales_order_number || "—"}</b></span>
                            <span>Posting Date: <b>{verify.posting_date || "—"}</b></span>
                            <span>Sales Org: <b>{verify.sales_org || "—"}</b>{verify.distribution_channel_text ? <span style={{ color: "#94A3B8" }}> · {verify.distribution_channel_text}</span> : null}</span>
                            <span>Customer PO: <b>{verify.buyer_id || "—"}</b></span>
                            <span>Net: <b>{verify.net_amount ? `${Number(verify.net_amount).toLocaleString()} ${verify.currency || ""}` : "—"}</b></span>
                          </div>
                          {verify.end_customer && (verify.end_customer.code || verify.end_customer.name) && (
                            <div data-testid="sap-verify-endcustomer" style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 8, fontSize: 12.5, color: "#475569" }}>
                              <span>End Customer: <b>{verify.end_customer.name || verify.end_customer.code}</b>{verify.end_customer.code && verify.end_customer.name ? <span style={{ color: "#94A3B8" }}> ({verify.end_customer.code})</span> : null}</span>
                              {verify.end_customer.po_ref && <span>End-Cust PO: <b>{verify.end_customer.po_ref}</b></span>}
                            </div>
                          )}
                          {(verify.delivery_blocking || verify.invoicing_blocking) && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 8, fontSize: 12, color: "#475569" }}>
                              {verify.delivery_blocking && <span>Delivery: <b style={{ color: /not blocked/i.test(verify.delivery_blocking) ? "#15803d" : "#b45309" }}>{verify.delivery_blocking}</b></span>}
                              {verify.invoicing_blocking && <span>Invoicing: <b style={{ color: /not blocked/i.test(verify.invoicing_blocking) ? "#15803d" : "#b45309" }}>{verify.invoicing_blocking}</b></span>}
                            </div>
                          )}
                          {Array.isArray(verify.items) && verify.items.length > 0 && (
                            <div className="sp-grid-wrap" style={{ marginTop: 10 }}>
                              <table className="sp-grid-table">
                                <thead><tr><th className="idx">#</th><th>Product</th><th>Description</th><th style={{ width: 90 }}>Req Qty</th><th style={{ width: 90 }}>Conf Qty</th><th style={{ width: 60 }}>Unit</th></tr></thead>
                                <tbody>
                                  {verify.items.map((it, i) => (
                                    <tr key={it.object_id || i} data-testid={`sap-verify-item-${i}`}>
                                      <td className="idx">{it.item || (i + 1) * 10}</td>
                                      <td>{it.product_id || "—"}</td>
                                      <td style={{ fontSize: 12 }}>{it.description || "—"}</td>
                                      <td>{it.requested_qty ?? "—"}</td>
                                      <td style={{ color: it.confirmed_qty == null ? "#b45309" : undefined }}>{it.confirmed_qty ?? "—"}</td>
                                      <td>{it.unit || "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            {verify.released ? (
                              <span data-testid="sap-verify-released" style={{ color: "#15803d", fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                                <CheckCircle size={18} weight="fill" /> Released in SAP — out of "In Preparation"
                              </span>
                            ) : (
                              <>
                                <button data-testid="sap-release-btn" className="jde-btn jde-btn-primary" disabled={releasing || !verify.releasable}
                                  onClick={() => doRelease(verify.order_id || result?.order_id)}>
                                  {releasing ? "Releasing…" : "Release in SAP"}
                                </button>
                                {!verify.releasable && (
                                  <span style={{ fontSize: 12, color: "#b45309" }}>
                                    Fix the red field(s) above first — an order releases only when every mandatory field is set and it's Consistent.
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Item valuation not active -> offer one-click activation in SAP */}
                  {valuationIssues.length > 0 && (
                    <div data-testid="sap-valuation-panel" className="sp-strip warn" style={{ marginTop: 10 }}>
                      <XCircle size={18} weight="fill" style={{ flexShrink: 0, marginTop: 1 }} />
                      <div style={{ flex: 1 }}>
                        <div className="sp-strip-title">Item valuation not active — SAP won't let the order release</div>
                        <div style={{ marginTop: 4 }}>
                          Activate the valuation (inventory cost 0 + Account Determination Group 3030, Business Residence <b>{form?.site_id || "?"}</b>), then re-run the Dry-run Check.
                        </div>
                        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {valuationIssues.map((v) => (
                            <button
                              key={`${v.product}|${v.company}`}
                              data-testid={`sap-activate-valuation-${v.product}`}
                              className="jde-btn"
                              disabled={activating === `${v.product}|${v.company}`}
                              onClick={() => activateValuation(v.product, v.company)}
                            >
                              {activating === `${v.product}|${v.company}` ? "Activating…" : `Activate valuation: ${v.product} (${v.company})`}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* OData header-completion warning: order created but Sales Order Date /
                      Business Residence could not be set (khsalesorder PATCH failed). */}
                  {result && result.ok && result.mode !== "check" && result.odata_header_update && !result.odata_header_update.ok && (
                    <div data-testid="sap-odata-header-warn" className="sp-strip warn" style={{ marginTop: 10 }}>
                      <XCircle size={18} weight="fill" style={{ flexShrink: 0, marginTop: 1 }} />
                      <div style={{ flex: 1 }}>
                        <div className="sp-strip-title">Order created, but Sales Order Date &amp; Business Residence were NOT set in SAP</div>
                        <div style={{ marginTop: 4 }}>
                          The SO landed in SAP as <b>“In Preparation”</b> with those two mandatory fields blank because the
                          post-create update failed:
                        </div>
                        <div className="sp-strip-err-line" style={{ marginTop: 4 }}>• {result.odata_header_update.reason || result.odata_header_update.message || "khsalesorder update failed"}</div>
                        {/RBAM|403|not authorized/i.test(result.odata_header_update.reason || result.odata_header_update.message || "") && (
                          <div style={{ marginTop: 6, opacity: 0.95 }}>
                            <b>Action (SAP admin):</b> the business user <code>AISAP</code> has lost its Access Rights for
                            Sales Order data in the <code>khsalesorder</code> OData service. Re-grant it a Sales Orders
                            work-center view with an Unrestricted (or P9/P2) access restriction, then push again — no app change needed.
                          </div>
                        )}
                        <div style={{ marginTop: 10 }}>
                          <button data-testid="sap-fix-header-btn" className="jde-btn jde-btn-primary" disabled={fixing}
                            onClick={() => fixHeader(result.order_id)}>
                            {fixing ? "Fixing…" : "Fix header now (retry)"}
                          </button>
                          <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.85 }}>
                            Re-runs only the header update on SO #{result.order_id} — never re-creates the order.
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Persistent pushed reference (kept for reference & tests) */}
                  {sel?.sap_so_id && !result && (
                    <div data-testid="sap-so-reference" className="sp-strip success">
                      <CheckCircle size={18} weight="fill" style={{ flexShrink: 0, marginTop: 1 }} />
                      <div style={{ flex: 1 }}>
                        <span className="sp-strip-title">Already pushed to SAP · Sales Order #{sel.sap_so_id}</span>
                        {sel.sap_so_uuid && <span className="sp-strip-mono"> · {sel.sap_so_uuid}</span>}
                        <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button data-testid="sap-verify-existing-btn" className="jde-btn jde-btn-primary" disabled={verifying}
                            onClick={() => loadVerify(sel.sap_so_id)}>
                            {verifying ? "Reading…" : "Verify & Release in SAP"}
                          </button>
                          <button data-testid="sap-fix-header-existing-btn" className="jde-btn" disabled={fixing}
                            onClick={() => fixHeader(sel.sap_so_id)}>
                            {fixing ? "Fixing…" : "Fix header (Date & Business Residence)"}
                          </button>
                        </div>
                        <span style={{ display: "block", marginTop: 6, fontSize: 12, opacity: 0.8 }}>
                          Use "Verify &amp; Release" to see what's written in SAP and push it out of "In Preparation".
                        </span>
                      </div>
                    </div>
                  )}

                  {/* ── Segmented tabs — section navigation (no vertical scrolling between sections) ── */}
                  <div className="sp-tabbar" data-testid="sap-detail-tabs">
                    {[
                      { id: "general", label: "General", icon: IdentificationCard },
                      { id: "end_customer", label: "End Customer", icon: IdentificationCard },
                      { id: "addresses", label: "Addresses", icon: MapPin },
                      { id: "sales_area", label: "Sales Area", icon: Buildings },
                      { id: "items", label: "Items", icon: Package, badge: form.lines.length },
                      { id: "history", label: "History", icon: ClockCounterClockwise },
                    ].map((t) => {
                      const Ico = t.icon;
                      return (
                        <button key={t.id} data-testid={`sap-tab-${t.id}`} className={`sp-tab-btn${tab === t.id ? " active" : ""}`}
                          onClick={() => { setTab(t.id); if (t.id === "history") loadHistory(); }}>
                          <Ico size={13} weight="bold" /> {t.label}
                          {t.badge != null && <span className="sp-tab-badge">{t.badge}</span>}
                        </button>
                      );
                    })}
                  </div>

                  {tab === "history" && (
                    <PushHistoryPanel history={history} historyOpen={historyOpen} setHistoryOpen={setHistoryOpen} historyLoading={historyLoading} loadHistory={loadHistory} historyExpanded={historyExpanded} setHistoryExpanded={setHistoryExpanded} />
                  )}

                  {/* General Information */}
                  <div className="sp-section" style={{ display: tabShow("general") }}>
                    <div className="sp-sec-h"><IdentificationCard size={14} weight="bold" /> General Information</div>
                    <div className="sp-sec-b">
                      <div className="sp-grid">
                        <div className="sp-fld"><label>Customer PO # (SAP BuyerID)</label><input data-testid="sap-f-external" className="jde-input" value={form.external_id} onChange={(e) => setF("external_id", e.target.value)} /></div>
                        <div className="sp-fld"><label>Order name (SAP Description · optional)</label><input data-testid="sap-f-name" className="jde-input" value={form.name} placeholder="blank — leave empty for no Description" onChange={(e) => setF("name", e.target.value)} /></div>
                        <div className="sp-fld"><label>Currency</label><input data-testid="sap-f-currency" className="jde-input" value={form.currency} onChange={(e) => setF("currency", e.target.value)} /></div>
                        <div className="sp-fld" style={{ gridColumn: "1 / -1" }}>
                          <label>SAP Customer / Account ID <span className="sp-req">*</span>
                            {sel && (sel.account_mapped
                              ? <span className="sp-tag ok">mapped</span>
                              : <span className="sp-tag warn">unmapped (raw code)</span>)}
                          </label>
                          <input data-testid="sap-f-account" className="jde-input" value={form.account_id} onChange={(e) => setF("account_id", e.target.value)} placeholder="auto from PO" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* End Customer — stamped to SAP custom fields (EndCustomerCode/Name/POReference).
                      Prefilled from the PO (direct) or the transfer-party resolution; fully editable. */}
                  <div className="sp-section" data-testid="sap-endcustomer-section" style={{ display: tabShow("end_customer") }}>
                    <div className="sp-sec-h"><IdentificationCard size={14} weight="bold" /> End Customer
                      {tpActive && <span className="sp-tag">from routing · {tpRouting?.tp_name}</span>}
                    </div>
                    <div className="sp-sec-b">
                      <div className="sp-grid">
                        <div className="sp-fld">
                          <label>End Customer Code</label>
                          <input data-testid="sap-f-endcust-code" className="jde-input" value={form.end_customer_code || ""}
                            onChange={(e) => setF("end_customer_code", e.target.value)} placeholder="e.g. MPY" />
                        </div>
                        <div className="sp-fld">
                          <label>End Customer PO Reference</label>
                          <input data-testid="sap-f-endcust-po" className="jde-input" value={form.end_customer_po_ref || ""}
                            onChange={(e) => setF("end_customer_po_ref", e.target.value)} placeholder="customer PO #" />
                        </div>
                        <div className="sp-fld" style={{ gridColumn: "1 / -1" }}>
                          <label>End Customer Name</label>
                          <input data-testid="sap-f-endcust-name" className="jde-input" value={form.end_customer_name || ""}
                            onChange={(e) => setF("end_customer_name", e.target.value)} placeholder="e.g. MACLEAN POWER -YORK L.L.C." />
                        </div>
                      </div>
                      <div className="sp-hint" style={{ marginTop: 6 }}>
                        Stamped on the SAP order at create; verified on read-back.
                      </div>
                    </div>
                  </div>
                  <div className="sp-section" data-testid="sap-address-resolution" style={{ display: tabShow("addresses") }}>
                    <div className="sp-sec-h"><MapPin size={14} weight="bold" /> Ship-To / Bill-To · resolved from SAP
                      {addrLoading && <span className="sp-tag">resolving…</span>}
                      {addrRes && !addrRes.sap_account && <span className="sp-tag warn">no SAP account mapped</span>}
                      {addrRes?.sap_error && <span className="sp-tag warn">SAP error</span>}
                    </div>
                    <div className="sp-sec-b">
                      {addrLoading && <div className="sp-hint">Matching the PO's addresses against live SAP…</div>}
                      {!addrLoading && !addrRes && <div className="sp-hint">Select a PO to resolve its addresses.</div>}
                      {!addrLoading && addrRes && (
                        <>
                          <div className="sp-hint" style={{ marginBottom: 8 }}>
                            Pick an address to send to SAP, or leave on <b>Don't send</b>.
                            {addrRes.sap_error && <span style={{ color: "var(--omh-warn,#b45309)" }}> · SAP: {addrRes.sap_error}</span>}
                          </div>
                          <div className="sp-grid">
                            <ResolveRow kind="ship" label="Ship-To (ProductRecipientParty)" block={addrRes.ship_to} sapAddresses={addrRes.sap_addresses} value={shipSel} onPick={setShipSel} twinSource={addrRes.twin_source} />
                            {tpActive ? (
                              <ResolveRow kind="bill"
                                label={`Bill-To (BillToParty) · ${tpRouting.tp_name}`}
                                block={{ po_text: tpBillLoading ? `Loading ${tpRouting.tp_name} addresses…` : `${tpRouting.tp_name} — sold-to & bill-to party`, candidates: [] }}
                                sapAddresses={tpBillAddrs || []}
                                value={billSel} onPick={setBillSel} />
                            ) : (
                              <ResolveRow kind="bill" label="Bill-To (BillToParty)" block={addrRes.bill_to} sapAddresses={addrRes.sap_addresses} value={billSel} onPick={setBillSel} twinSource={addrRes.twin_source} />
                            )}
                          </div>
                          {tpActive && (
                            <div data-testid="sap-billto-pin" style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 12 }}>
                              {billIsPinned ? (
                                <>
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "var(--omh-ok,#15803d)", fontWeight: 600 }}>
                                    <PushPin size={13} weight="fill" /> This is {tpRouting.tp_name}'s pinned default Bill-To
                                  </span>
                                  <button data-testid="sap-billto-unpin-btn" className="jde-btn" disabled={pinning} style={{ fontSize: 11.5, padding: "3px 10px" }} onClick={unpinBillTo}>
                                    {pinning ? "…" : "Unpin"}
                                  </button>
                                </>
                              ) : (
                                <>
                                  <span style={{ color: "#64748B" }}>{tpBillPinned ? "A different address is pinned as default." : "Pin this Bill-To so it stops defaulting to a branch address."}</span>
                                  <button data-testid="sap-billto-pin-btn" className="jde-btn jde-btn-primary" disabled={pinning || !billSel} style={{ fontSize: 11.5, padding: "3px 10px", display: "inline-flex", alignItems: "center", gap: 5 }} onClick={pinBillTo}>
                                    <PushPin size={13} weight="bold" /> {pinning ? "Pinning…" : "Pin as default Bill-To"}
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Sales Area */}
                  <div className="sp-section" style={{ display: tabShow("sales_area") }}>
                    <div className="sp-sec-h"><Buildings size={14} weight="bold" /> Sales Area</div>
                    <div className="sp-sec-b">
                      {showMapFix && (
                        <div data-testid="sap-map-fix-banner" style={{
                          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                          marginBottom: 12, padding: "10px 12px", borderRadius: 8,
                          background: "var(--omh-warn-bg,#fffbeb)", border: "1px solid var(--omh-warn,#f59e0b)",
                        }}>
                          <span style={{ fontSize: 13, color: "var(--omh-warn-fg,#92400e)", flex: 1, minWidth: 220 }}>
                            {noMap ? (
                              <>No saved sales-org mapping for <b>{mapInfo.account || mapInfo.app_code}</b>. SAP history uses <b>{sapSU}</b>{sapDC ? <> / <b>{sapDC}</b></> : null}.</>
                            ) : (
                              <>SAP history shows <b>{mapInfo.account || mapInfo.app_code}</b> uses <b>{sapSU}</b>{sapDC ? <> / <b>{sapDC}</b></> : null}, but the saved mapping is <b>{mappedSU || "—"}</b>{mappedDC && mappedDC !== sapDC ? <> / <b>{mappedDC}</b></> : null}.</>
                            )}
                          </span>
                          {mapInfo.saveError && <span className="sp-tag warn">save failed — retry</span>}
                          <button data-testid="sap-map-fix-btn" className="jde-btn" disabled={mapSaving}
                            onClick={applyMapCorrection}
                            style={{ background: "var(--omh-warn,#f59e0b)", color: "#fff", border: "none", padding: "6px 14px", borderRadius: 6, fontWeight: 600, cursor: mapSaving ? "default" : "pointer", whiteSpace: "nowrap" }}>
                            {mapSaving ? "Saving…" : noMap ? "Save mapping" : "Update mapping"}
                          </button>
                        </div>
                      )}
                      {mapInfo?.saved && !showMapFix && (
                        <div data-testid="sap-map-fix-done" className="sp-hint" style={{ marginBottom: 12, color: "var(--omh-ok,#15803d)", fontWeight: 600 }}>
                          ✓ Saved mapping updated to {sapSU}{sapDC ? ` / ${sapDC}` : ""} — future pushes for this customer start correct.
                        </div>
                      )}
                      <div className="sp-grid">
                        <div className="sp-fld">
                          <label>Sales Unit / Sales Org ID <span className="sp-req">*</span>
                            {salesArea?.ok && salesArea.sales_unit?.value && <span className="sp-tag" style={{ marginLeft: 6 }}>from SAP</span>}
                          </label>
                          <select data-testid="sap-f-seller" className="jde-select" value={form.seller_id} onChange={(e) => {
                            const v = e.target.value;
                            const site = siteFromSalesUnit(v);
                            setSoAutoFix(null);
                            setForm((f) => ({ ...f, seller_id: v, ...(site ? { site_id: site } : {}) }));
                          }}>
                            {!form.seller_id && <option value="">— select sales org —</option>}
                            {soOptionVals.map((v) => (
                              <option key={v} value={v}>{soLabel(v)}</option>
                            ))}
                          </select>
                          {soAutoFix && (
                            <div className="sp-hint" data-testid="sap-so-autofix" style={{ color: "var(--omh-ok,#15803d)", fontWeight: 600 }}>
                              ✓ Set to <code>{soAutoFix.to}</code> — the sales org valid for all items on this PO{soAutoFix.from ? <> (switched from {soAutoFix.from})</> : null}.
                            </div>
                          )}
                          <SaHint fld="sales_unit" />
                          {!soAutoFix && form.expected_seller && form.seller_id && form.seller_id !== form.expected_seller && (
                            <div className="sp-hint" data-testid="sap-seller-warn" style={{ color: "var(--omh-warn, #b45309)", fontWeight: 600 }}>
                              ⚠ Doesn't match this customer's mapped sales org (<code>{form.expected_seller}</code>). Pushing under a different sales org can fail currency / business-residence determination.
                            </div>
                          )}
                        </div>
                        <div className="sp-fld">
                          <label>Distribution Channel
                            {salesArea?.ok && salesArea.distribution_channel?.value && <span className="sp-tag" style={{ marginLeft: 6 }}>from SAP</span>}
                          </label>
                          <input data-testid="sap-f-dc" className="jde-input" list="sap-dc-list" value={form.distribution_channel} onChange={(e) => setF("distribution_channel", e.target.value)} />
                          <datalist id="sap-dc-list">
                            {(salesArea?.distribution_channel?.candidates || []).map((c) => (
                              <option key={c.value} value={c.value}>{c.value} · used {c.count}×</option>
                            ))}
                          </datalist>
                          <SaHint fld="distribution_channel" />
                        </div>
                        <div className="sp-fld">
                          <label>Employee Responsible {!form.employee_id && <span className="sp-tag warn">recommended</span>}</label>
                          <select data-testid="sap-f-employee" className="jde-select" value={form.employee_id} onChange={(e) => setF("employee_id", e.target.value)}>
                            <option value="">— none —</option>
                            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.label} · {emp.id}</option>)}
                          </select>
                        </div>
                        <div className="sp-fld">
                          <label>Ship-From Site {sitesLoading && <span className="sp-tag">loading…</span>}
                            {salesArea?.ok && salesArea.ship_from_site?.value && <span className="sp-tag" style={{ marginLeft: 6 }}>from SAP</span>}
                          </label>
                          {(() => {
                            const known = ["P1", "P2", "P3", "P4", "P5", "P8", "P9"];
                            const bySrc = {};
                            sites.forEach((s) => { bySrc[s.id] = s; });
                            const saCands = salesArea?.ok ? (salesArea.ship_from_site?.candidates || []) : [];
                            const saCount = {};
                            saCands.forEach((c) => { saCount[c.value] = c.count; });
                            const ids = [...new Set([...saCands.map((c) => c.value), ...sites.map((s) => s.id), ...known, form.site_id].filter(Boolean))];
                            const labelFor = (id) => {
                              if (saCount[id] != null) return `${id} · SAP history ${saCount[id]}×`;
                              const s = bySrc[id];
                              if (s?.source === "history") return `${id} · used ${s.count}×`;
                              if (s?.source === "determined") return `${id} · SAP determined`;
                              if (s?.source === "model") return `${id} · SAP source`;
                              return id;
                            };
                            return (
                              <>
                                <input data-testid="sap-f-site" className="jde-input" list="sap-site-list"
                                  placeholder="e.g. P4 — type or pick a site"
                                  value={form.site_id || ""}
                                  onChange={(e) => { setSiteTouched(true); setF("site_id", e.target.value.toUpperCase()); }} />
                                <datalist id="sap-site-list">
                                  {ids.map((id) => <option key={id} value={id}>{labelFor(id)}</option>)}
                                </datalist>
                              </>
                            );
                          })()}
                          <SaHint fld="ship_from_site" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="sp-section" style={{ display: tabShow("general") }}>
                    <div className="sp-sec-h"><CalendarBlank size={14} weight="bold" /> Dates</div>
                    <div className="sp-sec-b">
                      <div className="sp-grid">
                        <div className="sp-fld"><label>Posting date (PO date)</label><DateField testid="sap-f-posting" value={form.posting_date} onChange={(v) => setF("posting_date", v)} /></div>
                        <div className="sp-fld"><label>Requested delivery date</label><DateField testid="sap-f-reqdate" value={form.requested_date} onChange={(v) => setF("requested_date", v)} /></div>
                      </div>
                    </div>
                  </div>

                  {/* Line Items */}
                  <div className="sp-section" style={{ display: tabShow("items") }}>
                    <div className="sp-sec-h"><Package size={14} weight="bold" /> Line Items · auto-mapped from the PO</div>
                    <div className="sp-sec-b" style={{ padding: 0 }}>
                      <div className="sp-grid-wrap">
                      <table className="sp-grid-table">
                        <thead>
                          <tr>
                            <th className="idx">#</th>
                            <th>SAP Product ID <span className="sp-req">*</span></th>
                            <th style={{ width: 96 }}>Qty</th>
                            <th style={{ width: 70 }}>Unit</th>
                            <th style={{ width: 100 }}>Price</th>
                            {tpActive && <th style={{ width: 128 }} data-testid="sap-grid-delta-head">PO → Transfer Δ</th>}
                            <th style={{ width: 150 }}>Delivery</th>
                          </tr>
                        </thead>
                        <tbody>
                          {form.lines.map((l, i) => {
                            const unmapped = sel?.lines?.[i] && !sel.lines[i].mapped;
                            const soLine = sel?.lines?.[i];
                            const soList = soLine?.sales_orgs || [];
                            const soBad = soList.length > 0 && form.seller_id && !soList.includes(form.seller_id);
                            return (
                              <tr key={i}>
                                <td className="idx">{i + 1}</td>
                                <td>
                                  <input data-testid={`sap-line-prod-${i}`} className="jde-input" style={unmapped ? { borderColor: "#FDE68A", background: "#FFFBEB" } : undefined} value={l.product_id} onChange={(e) => setLine(i, "product_id", e.target.value)} title={unmapped ? "Unmapped — using raw app code. Map it on the SAP Mapping page." : ""} />
                                  {soBad && (
                                    <div data-testid={`sap-line-so-warn-${i}`} style={{ fontSize: 10.5, color: "#B45309", marginTop: 3 }}>
                                      ⚠ Not sellable under <code>{form.seller_id}</code> — valid sales orgs: {soList.join(", ")}
                                    </div>
                                  )}
                                </td>
                                <td><input data-testid={`sap-line-qty-${i}`} className="jde-input" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} /></td>
                                <td><input className="jde-input" value={l.unit_code} onChange={(e) => setLine(i, "unit_code", e.target.value)} /></td>
                                <td>
                                  <input data-testid={`sap-line-price-${i}`} className="jde-input" style={tpActive && tpRouting?.lines?.[i]?.needs_transfer_price ? { borderColor: "#FECACA", background: "#FEF2F2" } : undefined} value={l.price} onChange={(e) => setLine(i, "price", e.target.value)} />
                                  {tpActive && tpRouting?.lines?.[i]?.needs_transfer_price && (
                                    <div data-testid={`sap-line-tp-flag-${i}`} style={{ fontSize: 10, color: "#B91C1C", fontWeight: 700, marginTop: 3 }}>
                                      {tpRouting.lines[i].tp_reason === "expired" ? `Transfer price expired${tpRouting.lines[i].tp_valid_until ? ` ${tpRouting.lines[i].tp_valid_until}` : ""}` : "No approved transfer price"}
                                    </div>
                                  )}
                                </td>
                                {tpActive && (() => {
                                  const po = directSnapshot.current?.prices?.[i];
                                  const rate = tpRouting?.lines?.[i]?.transfer_rate;
                                  const blocked = tpRouting?.lines?.[i]?.needs_transfer_price;
                                  const v = tpVariancePct(rate, po);
                                  const flag = v != null && Math.abs(v) > TP_VAR_THRESHOLD;
                                  return (
                                    <td data-testid={`sap-line-delta-${i}`} style={{ fontSize: 11.5, lineHeight: 1.35 }}>
                                      {blocked || po == null || po === "" || rate == null ? (
                                        <span style={{ color: "#9CA3AF" }}>—</span>
                                      ) : (
                                        <>
                                          <div style={{ color: "#6B7280" }}>
                                            <span style={{ textDecoration: "line-through" }}>{Number(po).toFixed(2)}</span>
                                            {" → "}
                                            <span style={{ fontWeight: 700, color: "#0F766E" }}>{Number(rate).toFixed(2)}</span>
                                          </div>
                                          {v != null && (
                                            <div style={{ fontWeight: 700, color: flag ? "#B91C1C" : (v < 0 ? "#166534" : "#6B7280"), display: "flex", alignItems: "center", gap: 3 }}>
                                              {flag && <Warning size={11} weight="bold" />}
                                              {v > 0 ? "+" : ""}{v.toFixed(1)}%
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </td>
                                  );
                                })()}
                                <td><DateField testid={`sap-line-deliv-${i}`} value={l.delivery_date} onChange={(v) => setLine(i, "delivery_date", v)} /></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sticky action footer */}
                <div className="sp-footer">
                  <span className="hint" data-testid="sap-twostep-hint" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Info size={13} weight="bold" /> Two steps: <b>Create &amp; Write to SAP</b>, then verify the read-back below and <b>Release</b>.
                  </span>
                  <div className="acts">
                    {tpBlocked && <span data-testid="sap-tp-push-blocked-hint" style={{ fontSize: 11.5, color: "#B91C1C", fontWeight: 600, alignSelf: "center", marginRight: 4 }}>Transfer price missing — fix or switch to Direct</span>}
                    <button data-testid="sap-check-btn" className="jde-btn" disabled={!!busy || tpBlocked} onClick={() => run("check")}>{busy === "check" ? "Checking…" : "Dry-run Check"}</button>
                    {(() => {
                      // Once this PO has been successfully created in SAP, lock the Create button so it
                      // can't be fired again for the same SO. It re-enables automatically when a new PO
                      // is picked (pick() resets `result`).
                      const soCreated = !!(result?.ok && result?.mode === "push" && result?.order_id && !result?.blocked);
                      return (
                        <button
                          data-testid="sap-push-btn"
                          className="jde-btn jde-btn-primary"
                          disabled={!!busy || !conn?.ok || tpBlocked || soCreated}
                          title={soCreated ? "This Sales Order is already created in SAP. Select a new PO to create another." : undefined}
                          onClick={runStream}
                        >
                          {busy === "push"
                            ? "Creating…"
                            : soCreated
                            ? `Created SO #${result.order_id} ✓`
                            : "Create & Write to SAP"}
                        </button>
                      );
                    })()}
                    {result?.ok && result?.mode !== "check" && result?.order_id && !result?.blocked && !verify?.released && (
                      <button data-testid="sap-footer-release-btn" className="jde-btn jde-btn-primary"
                        style={{ background: "var(--omh-ok,#15803d)", borderColor: "var(--omh-ok,#15803d)" }}
                        disabled={!!busy || releasing} onClick={() => doRelease(result.order_id)}>
                        {releasing ? "Releasing…" : `Release SO #${result.order_id}`}
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Stored PO document viewer */}
        {fileOpen && (
          <div data-testid="sap-po-file-modal" onClick={() => setFileOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: "#fff", borderRadius: 12, width: "min(960px, 96vw)", height: "min(86vh, 900px)", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,0.35)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #E5E7EB" }}>
                <div style={{ fontWeight: 700, color: "#0F172A", display: "flex", alignItems: "center", gap: 8 }}>
                  <FileText size={16} weight="bold" /> PO document · {sel?.po_number || ""}
                </div>
                <button data-testid="sap-po-file-close" onClick={() => setFileOpen(false)}
                  style={{ border: "none", background: "none", cursor: "pointer", color: "#64748B", display: "flex" }}>
                  <X size={20} weight="bold" />
                </button>
              </div>
              <div style={{ flex: 1, minHeight: 0, overflow: "auto", background: "#F8FAFC" }}>
                {fileState === "loading" && <div style={{ padding: 28, color: "#64748B" }}>Loading the stored PO document…</div>}
                {fileState === "none" && (
                  <div data-testid="sap-po-file-none" style={{ padding: 28, color: "#475569", lineHeight: 1.6 }}>
                    <div style={{ fontWeight: 700, color: "#0F172A", marginBottom: 6 }}>No original PO document found for this PO.</div>
                    {addrRes?.po?.source === "excel"
                      ? <>This PO came from a <b>bulk Excel import</b>
                          {addrRes?.po?.source_file ? <> (<code>{addrRes.po.source_file}</code>)</> : ""}, which carries line items only — there is no per-PO PDF/email to show.</>
                      : <>This PO was received via <b>email / PDF intake</b>, so the original document should be on file. We also checked its earlier revisions (via the change-of-PO chain) and still couldn't locate it — the capture likely failed at intake. Try re-running intake for this PO, or contact support with the PO number.</>}
                  </div>
                )}
                {fileState === "error" && <div style={{ padding: 28, color: "#DC2626" }}>Could not load the PO document. Please try again.</div>}
                {fileState === "ok" && fileMime === "application/pdf" && (
                  <iframe title="po-file" src={fileUrl} data-testid="sap-po-file-pdf" style={{ width: "100%", height: "100%", border: "none" }} />
                )}
                {fileState === "ok" && fileMime.startsWith("image/") && (
                  <div style={{ padding: 16, textAlign: "center" }}><img alt="po-file" src={fileUrl} style={{ maxWidth: "100%" }} /></div>
                )}
                {fileState === "ok" && fileMime !== "application/pdf" && !fileMime.startsWith("image/") && (
                  fileText
                    ? <pre data-testid="sap-po-file-text" style={{ whiteSpace: "pre-wrap", padding: 20, fontSize: 13, color: "#0F172A", fontFamily: "inherit", margin: 0 }}>{fileText}</pre>
                    : <div style={{ padding: 28, color: "#475569" }}>
                        This file type can't preview inline{fileName ? ` (${fileName})` : ""}.{" "}
                        <a href={fileUrl} download data-testid="sap-po-file-download" style={{ color: "#2563EB", fontWeight: 600 }}>Download it</a> to view.
                      </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
